// packages/charts/src/optionBuilder.ts
//
// `BackendChartSpec` -> `EChartsOption`. The whole of it, and nothing else.
//
// MOVED, NOT REWRITTEN (2026-09-08, plan 2026-09-08-one-drawing-layer-for-
// chat-dashboard-and-block-reports, A2). Every function below is byte-for-byte
// what stood in `apps/frontend/src/components/chat/message/chart/
// ChartWithControls.tsx` lines 72-2920 before the move, minus the React hook
// `useChartPreference` (which stayed with the component, being a hook) and
// with `import` paths repointed. The claim "nothing changed" is not an
// assertion of care: `ChartWithControls.goldens.test.tsx` (25 fixtures x
// builder option + component option + rendered SVG) and
// `src/react/__tests__/goldens.test.tsx` must stay
// byte-identical across the move, and they do.
//
// WHY IT IS A PACKAGE. Three surfaces draw charts — a chat message, a
// dashboard tile and a block report — and until today the third had its own
// drawing stack, four chart types behind the other two. The render layer may
// differ per surface (a chart in a tile, in a message, on a page); everything
// above it — the spec, this option builder, the formats — may not.
//
// WHAT MAY NOT ENTER THIS FILE. No React, no Mantine, no `apps/`, and ECharts
// as `import type` only: the report sandbox loads `echarts/core` with an
// explicit `use()` list, so a value import here would pull the full entry into
// a mailed bundle. `eslint.config.mjs` in this package enforces all four.
//
// NO ENVIRONMENT READS EITHER (A3). Until 2026-09-08 this file read
// `window.matchMedia("(prefers-reduced-motion: reduce)")` at import time. That
// guard answered "animate" in the renderer's Node process and in the mailed
// bundle — the two places where animation is only latency before the SVG is
// drawn. Motion is now the builder's LAST argument (`motion: "full" | "none"`);
// the app passes its preference (`components/charts/motion.ts`),
// the e-mail runtime passes `"none"`. `tsconfig.json` here has no `DOM` lib,
// so a new `window.` or `document.` is a type error, not a guard someone has
// to remember. `rowPreviews` — the Shopify product hover images — stays an
// optional builder parameter, present on the dashboard and absent everywhere
// else; it never becomes a spec field, because it is the only `<img>` sink in
// the tooltip.
import type { EChartsOption } from "echarts";

import {
  buildBarTooltipFormatter,
  escapeHtml,
  type RowPreview,
} from "./chartTooltip";
import type {
  BackendChartSpec,
  SeriesConfig,
  BarEndLabelSingle,
  BarEndLabelMulti,
} from "./types";
import {
  computeComparisonDelta,
  DELTA_NEGATIVE_COLOR,
  DELTA_NEUTRAL_COLOR,
  DELTA_POSITIVE_COLOR,
} from "./comparisonDelta";
// Type-only: `DashboardChartType` is `(typeof CHART_STYLES)[number]`, the
// derived union of every chart style a dashboard tile can be stored as. It is
// what makes `SERIES_BUILDERS` below a TOTAL map instead of a lookup table
// with holes. Erased at compile time, so it adds no module edge.
//
// It lives in `@verity/shared-types` rather than in the frontend's
// `composition/blocks.ts` precisely so that this file can read it: blocks.ts
// imports the composition HTTP client, and this package may not import from
// `apps/`. blocks.ts re-exports it, so the app is unchanged.
import type { DashboardChartType } from "@verity/shared-types/chart-styles";
import { NUMBER_LOCALE } from "./numberLocale";
import { DEFAULT_CHART_COLORS } from "./colors";
import {
  findHighlight,
  sentimentColor,
  sentimentBgColor,
  supportsHighlights,
  barGradient,
} from "./highlightUtils";
import {
  INCOMPLETE_PERIOD_COLOR,
  buildIncompleteZoneMarkArea,
  buildIncompleteZoneMarkLine,
} from "./incompletePeriodUtils";
import { readTimeAxis } from "./timeAxis";
import { datePartLabellerFor, withDatePartSeriesLabels } from "./datePart";
import { funnelSteps, sortAndTrimDateRows } from "./drawnRows";
import { buildKpiPictureOption, isKpiPictureChartType } from "./kpiPictures";
import { buildSankeyOption } from "./sankey";
import {
  SCATTER_NOT_SET_COLOR,
  groupScatterSeries,
  measuredNumber,
  placeScatterRows,
} from "./scatterSeries";
export { SCATTER_MAX_SERIES, SCATTER_NOT_SET_COLOR, SCATTER_NOT_SET_LABEL } from "./scatterSeries";
// Every SERIES colour this file paints (a series, slice, tile, step, node,
// group, or a waterfall/target role) comes either from its series ref's
// resolved colour (a dashboard tile's `colorLayers`, ADR-0476) or from a
// positional pick out of `./seriesColors.ts`, the one module allowed to index
// a palette (`__tests__/paletteIndexGuard.test.ts`). Fixed colours that are
// not a series stay constants here: the delta green/red/grey, the "(not set)"
// grey, the highlight sentiments, the incomplete-period amber and the `#666`
// axis furniture.
import {
  cartesianSeriesRef,
  dimensionValueRef,
  distributedPick,
  funnelStepRef,
  positionalColor,
  resolveChartColors,
  resolveSeriesColors,
  sankeyNodeRef,
  seriesRefsOf,
  scatterGroupRef,
  selectDistributedColors,
  stepWaterfallColors,
  type ChartColorLayers,
  type SeriesRef,
} from "./seriesColors";
import { SYNTHETIC_ROW, attachChartDatums, type DatumInteraction } from "./datumClick";

// ------------------------------------------------------------
// Animation helpers
// ------------------------------------------------------------

/** Whether an option animates. `"none"` is what a surface passes when nobody
 *  will see the motion (a mailed report, a print) or when the reader asked for
 *  none (`prefers-reduced-motion`); the caller decides, this file never asks
 *  the environment. */
export type ChartMotion = "full" | "none";

type ChartAnimationType =
  | "pie" | "bar" | "horizontal-bar" | "line" | "funnel" | "combo" | "scatter" | "treemap" | "sankey";

function chartAnimation(type: ChartAnimationType, motion: ChartMotion) {
  if (motion === "none") return { animation: false as const };

  const base = {
    animation: true as const,
    animationEasing: "cubicOut" as const,
    animationDurationUpdate: 200,
  };

  switch (type) {
    case "pie":
      return { ...base, animationDuration: 400 };
    case "bar":
      return {
        ...base,
        animationDuration: 600,
        animationDelay: (idx: number) => idx * 50,
      };
    case "horizontal-bar":
      return {
        ...base,
        animationDuration: 600,
        animationDelay: (idx: number) => idx * 60,
      };
    case "line":
      // Line draw animation is handled at series level (see series config)
      // because chart-level animationDelay doesn't stagger individual line points.
      return { ...base, animationDuration: 600 };
    case "combo":
      return {
        ...base,
        animationDuration: 600,
        animationDelay: (idx: number) => idx * 30,
      };
    case "funnel":
      return {
        ...base,
        animationDuration: 500,
        animationDelay: (idx: number) => idx * 80,
      };
    case "scatter":
    case "treemap":
    case "sankey":
      // A plain fade, with NO `animationDelay`. Every staggered variant above
      // reveals its marks in index order, which reads as a sequence — right
      // for bars along an axis and for funnel stages, wrong here: a scatter's
      // points are unordered and a treemap's tiles are ranked by area, so a
      // stagger would announce an order the picture does not have.
      return { ...base, animationDuration: 400 };
    default:
      return { ...base, animationDuration: 600 };
  }
}

// ------------------------------------------------------------
// Bar end label helpers
// ------------------------------------------------------------

export function normalizeBarEndLabels(
  labels: BarEndLabelSingle | BarEndLabelMulti
): BarEndLabelMulti {
  if ("columns" in labels) return labels;
  return { columns: [labels], default: labels.column };
}

// ------------------------------------------------------------
// Helpers
// ------------------------------------------------------------
export const DEFAULT_MAX_CATEGORIES = 10;
export const MAX_CHART_ROWS = 100; // Default limit for bar-type charts
export const MAX_CHART_ROWS_LINE = 500; // Higher limit for line/timeseries (many x-points are fine)
const HORIZONTAL_BAR_VISIBLE_CATEGORIES = 6; // Show 6 categories by default, scroll for more

/**
 * Chart types this component renders through the ECharts option builder.
 * Types with their own renderer (metric_card, table, funnel_explorer,
 * kpi_period_table) are routed before this set is consulted; everything else
 * gets the unsupported-chart empty state rather than a fall-through render.
 * The empty string keeps the pre-existing behaviour for specs that omit
 * chart_type entirely (they render as a bar chart via chooseBarType).
 *
 * That promise was FALSE for two members until 2026-09-07: `heatmap` and
 * `scatter` sat in this set with no branch anywhere in this file, so they
 * passed the gate and fell through to `defaultSeriesType = "bar"` — measured,
 * their option was identical to `bar`'s. Membership here is a claim that a
 * branch exists, and `ChartWithControls.rendererCoverage.test.ts` now makes
 * every member prove it (plan 2026-09-07 chart library, S1c).
 */
export const ECHARTS_SERIES_CHART_TYPES = new Set<string>([
  "",
  "pie",
  "donut",
  "line",
  "line_smoothed",
  "area",
  "area_stacked",
  "bar",
  "grouped-bar",
  "grouped_bar",
  "stacked-bar",
  "stacked_bar",
  "horizontal-bar",
  "horizontal_bar",
  "horizontal-grouped-bar",
  "horizontal-stacked-bar",
  "timeseries",
  "category_comparison",
  "combo",
  // Registry rows still on `status="library"`: the branch below exists, the
  // composer cannot stamp them yet (a library row is filtered out of
  // `CHART_STYLES`). Accepted here so that the flip to `available` is a
  // one-line registry change and not also a renderer change — and so a tile
  // that already carries the spelling draws its picture rather than the
  // "not supported yet" empty state.
  "bar_stacked_100",
  "waterfall",
  // Drawn by the waterfall builder through its OWN recipe, which refuses
  // rows that carry no anchor. See `buildBridgeSeries`.
  "bridge",
  // The step waterfall (plan 2026-10-16-a-waterfall-with-subtotals): levels
  // and steps the server built from one row. See `buildStepWaterfallSeries`.
  "metric_waterfall",
  "scatter",
  "bubble",
  "treemap",
  // Plan 2026-10-05 finance chart types, C3: drawn by `./sankey.ts`.
  "sankey",
  // Plan 2026-10-09 standard funnel, D3: drawn by `buildFunnelOption`. Until
  // then it had its own dashboard component and a route of its own.
  "funnel",
  // Plan 2026-10-17 stacked metrics: the parts of one total, stacked per
  // period. Drawn by the shared stacked loop plus a total label (absolute
  // only) and a "Total" tooltip line — see `PARTS_TOTAL_LABEL_SERIES_NAME`.
  "bar_stacked_metrics",
  "bar_stacked_metrics_100",
]);

/**
 * The line family, split into the two flavours the builder actually draws
 * differently: a plain polyline, and a polyline whose FILL is the point of the
 * chart. Six sites used to answer "is this a line?" with their own
 * `=== "line" || === "timeseries" || === "area"` chain (the builder's flags,
 * the toggle's seed, the line-mode collapse, the smooth switch, the row cap,
 * the reveal animation), so a new line variant had to be remembered in six
 * places — and the site that forgot it did not fail, it drew something else.
 * The sets here are the only place a spelling is listed; every site below
 * reads the predicates instead.
 */
const AREA_CHART_TYPES = new Set<string>(["area", "area_stacked"]);
const PLAIN_LINE_CHART_TYPES = new Set<string>(["line", "timeseries", "line_smoothed"]);
/**
 * Chart types whose curve is smoothed BY THE TYPE — the reader picked
 * "Smoothed line", not "line, and then I flipped a switch". The Smooth switch
 * stays the way a plain line is smoothed on demand.
 */
const SMOOTHED_CHART_TYPES = new Set<string>(["line_smoothed"]);

const lower = (chartType: string | undefined | null): string => (chartType || "").toLowerCase();

/** An area chart: a line whose fill carries the message, so it gets a visible one. */
export const isAreaFamily = (chartType: string | undefined | null): boolean =>
  AREA_CHART_TYPES.has(lower(chartType));
/** Anything the builder draws with `type: "line"` — areas included. */
export const isLineFamily = (chartType: string | undefined | null): boolean =>
  PLAIN_LINE_CHART_TYPES.has(lower(chartType)) || isAreaFamily(chartType);
/**
 * A plain polyline, areas excluded: the only shape where offering to smooth
 * the curve makes sense. Areas have never had the switch and do not get one.
 */
export const isPlainLineFamily = (chartType: string | undefined | null): boolean =>
  PLAIN_LINE_CHART_TYPES.has(lower(chartType));
/** The curve is decided by the chart type, not by the Smooth switch. */
export const isSmoothedByChartType = (chartType: string | undefined | null): boolean =>
  SMOOTHED_CHART_TYPES.has(lower(chartType));

/**
 * Chart types the line/bar toggle opens on "Line" for — DERIVED from the sets
 * above rather than hand-kept beside them, which is what let `area` be a line
 * to the builder and a bar to the toggle. Exported because the renderer
 * coverage test asserts every member of it is a member of the accepted set.
 */
export const LINE_MODE_CHART_TYPES = new Set<string>([...PLAIN_LINE_CHART_TYPES, ...AREA_CHART_TYPES]);

/**
 * A stack: its marks add up per position. The builder stacks every series of
 * such a spelling (`stack: "total"`), `chooseBarType` keeps it a stack across
 * the Line | Bar toggle, and the display chain draws every key by default
 * (plan 2026-10-17 stacked metrics, S2b: a part you cannot see is a column
 * that no longer adds up to the total it shows). One predicate for all three,
 * so they cannot disagree about what a stack is.
 */
export const isStackedChartType = (chartType: string | undefined | null): boolean =>
  lower(chartType).includes("stack");

/**
 * Stacks whose rows are rewritten to each column's shares before the shared
 * loop draws them (`normalizeRowsTo100Percent`): every column is 100%.
 */
export const HUNDRED_PERCENT_STACK_CHART_TYPES: ReadonlySet<string> = new Set([
  "bar_stacked_100",
  "bar_stacked_metrics_100",
]);

/**
 * Above this many periods the total labels of a stacked metrics chart collide
 * (ECharts' `labelLayout.hideOverlap` needs the `LabelLayout` feature, which
 * the report sandbox does not register). The tooltip's "Total" line carries
 * the sum at every size. Fixed at the browser gate on a full-width tile.
 */
export const MAX_TOTAL_LABEL_PERIODS = 24;

/** The invisible series that carries a stacked metrics chart's total labels.
 *  Exported for the skeleton, which drops it (it draws nothing but labels). */
export const PARTS_TOTAL_LABEL_SERIES_NAME = "_parts_total";

/** The colour of the value axes' labels, which the total label shares. */
const AXIS_LABEL_COLOR = "#999";

/**
 * Comparison bars sit right against their own actual with a slight overlap, so a
 * metric and its previous-period value read as one pair.
 *
 * ECharts takes ONE barGap for the whole bar group on an axis (layout/barGrid.js:
 * "only the attributes set on the last series will work"), so a wider gap between
 * pairs cannot be expressed as a second gap value — it needs an empty bar column
 * between the pairs. A data-less bar series still reserves a column, which is what
 * `comparisonGapSeries` is for. With the overlap below the space between two pairs
 * comes out at 0.6x a bar width, scaling with the bars instead of a pixel constant.
 */
const COMPARISON_BAR_GAP = "-20%";
/**
 * Space between categories (days, products) once pairs are in play.
 *
 * ECharts derives the category gap from the number of columns —
 * `Math.max(35 - columnCount * 4, 15) + "%"` (layout/barGrid.js) — so adding the
 * comparison and gap columns shrinks it to the 15% floor, and the space between
 * two days ends up the same as the space between two pairs inside a day. The
 * grouping then reads as one long row of bars. Pinning it keeps the hierarchy
 * intact: overlap inside a pair < gap between pairs < gap between categories.
 * Measured on a 7-day, 2-metric tile: 32 px between days vs 8.6 px between pairs.
 */
const COMPARISON_BAR_CATEGORY_GAP = "35%";
/**
 * Tooltip text, one step below the ECharts default of 14px. The hover carries a
 * product image next to it, and at the default size the text competed with the
 * picture for the reader's attention instead of labelling it.
 */
const TOOLTIP_FONT_SIZE = 12;
const GAP_SERIES_PREFIX = "_gap_";
/**
 * The horizontal bound of a guardrail (comparison_mode="target", kind
 * "guardrail"). Deliberately NOT the amber of the incomplete-period line: the
 * two can sit on the same chart, and one colour for two unrelated statements
 * is how a reader learns to ignore both.
 */
const GUARDRAIL_LINE_COLOR = "#7c3aed";
/** Helper series never appear in the legend or the tooltip. */
const isHelperSeriesName = (name: unknown): boolean => typeof name === "string" && name.startsWith("_");

/** Invisible bar column that separates one actual/comparison pair from the next. */
function comparisonGapSeries(index: number, rowCount: number, barMaxWidth: number): any {
  return {
    name: `${GAP_SERIES_PREFIX}${index}`,
    type: "bar",
    barCategoryGap: COMPARISON_BAR_CATEGORY_GAP,
    silent: true,
    legendHoverLink: false,
    barMaxWidth,
    itemStyle: { color: "transparent" },
    emphasis: { disabled: true },
    tooltip: { show: false },
    data: Array.from({ length: rowCount }, () => null),
    z: 0,
  };
}

/** Build dataZoom config for scrollable horizontal bar charts */
function horizontalBarDataZoom(categoryCount: number, yAxisIndices: number[] = [0], visibleCount: number = HORIZONTAL_BAR_VISIBLE_CATEGORIES): any[] {
  if (categoryCount <= visibleCount) return [];
  return [
    {
      type: "slider",
      yAxisIndex: yAxisIndices,
      right: 0,
      width: 12,
      startValue: 0,
      endValue: visibleCount - 1,
      brushSelect: false,
      handleSize: "60%",
      showDetail: false,
      borderColor: "transparent",
      fillerColor: "rgba(0,0,0,0.05)",
      handleStyle: { color: "#c0c0c0", borderColor: "#c0c0c0" },
    },
    {
      type: "inside",
      yAxisIndex: yAxisIndices,
      startValue: 0,
      endValue: visibleCount - 1,
      zoomOnMouseWheel: false,
      moveOnMouseWheel: true,
    },
  ];
}

/** Map a delta colour onto its ECharts rich-text style key. */
const deltaRichStyle = (color: string): string =>
  color === DELTA_POSITIVE_COLOR
    ? "deltaUp"
    : color === DELTA_NEGATIVE_COLOR
      ? "deltaDown"
      : "deltaFlat";

export const isTimeKey = (k?: string) =>
  !!k &&
  /date|datum|day|dag|year|jaar|month|maand|week|quarter|kwartaal|year[_-]?month|year[_-]?week|year[_-]?quarter|jaar[_-]?maand|jaar[_-]?week|jaar[_-]?kwartaal/i.test(
    k
  );

const toStr = (v: any) => (v === null || v === undefined ? "" : String(v));
export const toNum = (v: any) => {
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : 0;
};

// Date formatting for x-axis labels
const MONTH_ABBR = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/**
 * Detect if date values represent daily data (varying days) or monthly/yearly aggregates.
 * Returns "daily" if days vary, "monthly" if only months/years vary.
 */
function detectDateGrain(values: string[]): "daily" | "monthly" {
  const datePattern = /^(\d{4})-(\d{2})-(\d{2})/;
  const days = new Set<string>();

  for (const v of values.slice(0, 10)) { // Check first 10 values
    const match = v.match(datePattern);
    if (match) {
      days.add(match[3]); // Day part
    }
  }

  // If we see multiple different days, it's daily data
  // If all days are "01", it's likely monthly aggregates
  if (days.size > 1) return "daily";
  if (days.size === 1 && days.has("01")) return "monthly";
  return "daily"; // Default to daily
}

/** Blend a hex colour towards white by `fraction` (0 = unchanged, 1 = white). */
function lightenHex(hex: string, fraction: number): string {
  const h = hex.replace("#", "");
  const r = parseInt(h.substring(0, 2), 16);
  const g = parseInt(h.substring(2, 4), 16);
  const b = parseInt(h.substring(4, 6), 16);
  const lr = Math.round(r + (255 - r) * fraction);
  const lg = Math.round(g + (255 - g) * fraction);
  const lb = Math.round(b + (255 - b) * fraction);
  return `#${lr.toString(16).padStart(2, "0")}${lg.toString(16).padStart(2, "0")}${lb.toString(16).padStart(2, "0")}`;
}

function getIsoWeek(yearFull: number, month1Indexed: number, day: number): { week: number; weekYear: number } {
  // ISO 8601 week number: week belongs to the year of its Thursday
  const d = new Date(Date.UTC(yearFull, month1Indexed - 1, day));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const weekYear = d.getUTCFullYear();
  const yearStart = new Date(Date.UTC(weekYear, 0, 1));
  const week = Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
  return { week, weekYear };
}

function formatDateLabel(value: string, grain: "daily" | "weekly" | "monthly" | "quarterly" | "yearly" = "monthly"): string {
  // Match YYYY-MM-DD or YYYY-MM-DD HH:MM:SS formats
  const dateMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (dateMatch) {
    const yearFull = parseInt(dateMatch[1], 10);
    const year = dateMatch[1].slice(2); // '25' from '2025'
    const month = parseInt(dateMatch[2], 10); // 1-indexed
    const day = parseInt(dateMatch[3], 10);

    if (grain === "yearly") {
      return `'${year}`;
    }
    if (grain === "quarterly") {
      const quarter = Math.ceil(month / 3);
      return `Q${quarter} '${year}`;
    }
    if (grain === "weekly") {
      const { week, weekYear } = getIsoWeek(yearFull, month, day);
      return `wk${week.toString().padStart(2, "0")} '${weekYear.toString().slice(2)}`;
    }
    if (grain === "daily") {
      // For daily data: "15 Jan"
      return `${day} ${MONTH_ABBR[month - 1]}`;
    }
    // For monthly data: "Jan '25"
    return `${MONTH_ABBR[month - 1]} '${year}`;
  }
  return value;
}

// Detect if values represent percentages (0-1 range or key suggests percentage)
function isPercentageKey(key: string): boolean {
  const lowerKey = key.toLowerCase();
  return /ratio|percent|percentage|pct|%|_rate$|_share$/.test(lowerKey);
}

function isPercentageData(values: number[]): boolean {
  // Check if all values are in 0-1 range (typical for raw fractions)
  if (values.length === 0) return false;
  const validValues = values.filter(v => Number.isFinite(v) && v !== 0);
  if (validValues.length === 0) return false;

  return validValues.every(v => v >= -1 && v <= 1);
}

/**
 * Detect if values are already-multiplied percentages (e.g. 2.5 for 2.5%).
 * True when key names suggest percentage but values are > 1.
 */
function isAlreadyPercentageData(values: number[], keys: string[]): boolean {
  if (!keys.some(isPercentageKey)) return false;
  const validValues = values.filter(v => Number.isFinite(v) && v !== 0);
  if (validValues.length === 0) return false;
  // If any value is > 1, it's already ×100
  return validValues.some(v => Math.abs(v) > 1);
}

function formatPercentage(value: number): string {
  return `${(value * 100).toFixed(1)}%`;
}

/**
 * Smart number formatting - only show decimals if needed.
 * E.g., 25.0 → "25", 25.5 → "25.5", 25.123 → "25.1"
 */
function smartNumber(value: number, maxDecimals: number = 1): string {
  const rounded = Math.round(value * Math.pow(10, maxDecimals)) / Math.pow(10, maxDecimals);
  // Check if it's a whole number
  if (Number.isInteger(rounded)) {
    return rounded.toString();
  }
  return rounded.toFixed(maxDecimals);
}

/**
 * Format Y-axis label based on the specified format type.
 * - "fraction": Multiply by 100 and show as percentage (0.23 → "23%")
 * - "percentage": Show as percentage (23 → "23%")
 * - "currency": Show with currency symbol
 * - Default: Abbreviated numbers (1M, 1K)
 *
 * Decimals are only shown when needed (e.g., 25% not 25.0%)
 */
function formatYAxisLabel(value: number, format?: string): string {
  if (format === "fraction") {
    return `${smartNumber(value * 100)}%`;
  }
  if (format === "percentage") {
    return `${smartNumber(value)}%`;
  }
  if (format === "currency") {
    if (Math.abs(value) >= 1_000_000) {
      return `€${smartNumber(value / 1_000_000)}M`;
    }
    if (Math.abs(value) >= 1_000) {
      return `€${smartNumber(value / 1_000)}K`;
    }
    return `€${smartNumber(value)}`;
  }
  // Default: abbreviated numbers
  if (Math.abs(value) >= 1_000_000) {
    return `${smartNumber(value / 1_000_000)}M`;
  }
  if (Math.abs(value) >= 1_000) {
    return `${smartNumber(value / 1_000)}K`;
  }
  return smartNumber(value);
}

/**
 * Presentation-layer reshape only — data is pre-aggregated by the backend.
 * Re-pivots un-pivoted rows into grouped-bar format using a different metric column.
 */
export function pivotRows(
  rawRows: Record<string, any>[],
  indexKey: string,
  breakdownKey: string,
  metricKey: string,
): Record<string, any>[] {
  const grouped = new Map<string, Record<string, any>>();
  for (const row of rawRows) {
    const idx = String(row[indexKey] ?? "");
    if (!grouped.has(idx)) grouped.set(idx, { [indexKey]: row[indexKey] });
    const breakdownVal = String(row[breakdownKey] ?? "");
    grouped.get(idx)![breakdownVal] = row[metricKey];
  }
  return [...grouped.values()];
}

/**
 * Format tooltip value based on the specified format type.
 * Tooltips show more precision than axis labels — full numbers with limited decimals.
 * Only abbreviate for very large values (≥1M) where the full number is unwieldy.
 *
 * Exported for unit tests (plan 2026-09-08-one-drawing-layer, A1a).
 */
export function formatTooltipValue(value: number, format?: string): string {
  if (format === "fraction") {
    return `${smartNumber(value * 100, 2)}%`;
  }
  if (format === "percentage") {
    return `${smartNumber(value, 2)}%`;
  }
  if (format === "currency") {
    if (Math.abs(value) >= 1_000_000) {
      return `€${smartNumber(value / 1_000_000, 2)}M`;
    }
    return `€${value.toLocaleString(NUMBER_LOCALE, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }
  // Default: full number with limited decimals, abbreviate only ≥1M
  if (Math.abs(value) >= 1_000_000) {
    return `${smartNumber(value / 1_000_000, 2)}M`;
  }
  // Locale pinned (en-US, the same constant the metric tables use): this
  // builder is shared with the report sandbox, whose formatter and the
  // Python side already pin it. Unpinned, the same number reads "1.234,5"
  // on an nl runtime and "1,234.5" on an en one — and the e-mail renderer's
  // Chromium is a third runtime nobody chose.
  if (Number.isInteger(value)) {
    return value.toLocaleString(NUMBER_LOCALE);
  }
  return value.toLocaleString(NUMBER_LOCALE, { minimumFractionDigits: 0, maximumFractionDigits: 2 });
}

/**
 * Format legend name - wrap long names over 2 lines.
 */
function formatLegendName(name: string, maxCharsPerLine: number = 15): string {
  if (name.length <= maxCharsPerLine) return name;

  const mid = Math.ceil(name.length / 2);
  // Find nearest space to middle for cleaner break
  const spaceAfter = name.indexOf(" ", mid);
  const spaceBefore = name.lastIndexOf(" ", mid);
  const breakPoint = spaceAfter !== -1 && (spaceAfter - mid) < (mid - spaceBefore)
    ? spaceAfter
    : spaceBefore !== -1 ? spaceBefore : mid;
  return name.slice(0, breakPoint) + "\n" + name.slice(breakPoint + 1);
}

// ---- the page legend -------------------------------------------------------
//
// On a REPORT PAGE the reader holds paper (or a PDF): nothing scrolls, nothing
// hovers, nothing can be clicked. ECharts' `type: "scroll"` legend answers a
// legend that does not fit with a pager ("3/3") — measured on the paid-media
// deep dive, two charts side by side on A4 portrait are 340px wide and three
// series already page — so entries end up behind a control nobody can reach.
//
// A plain VERTICAL legend is not the answer either, and this is the trap the
// step exists for: a plain legend does not paginate, so a vertical one simply
// runs past the bottom of the canvas, and the page cell clips it
// (`sections.ts`: every cell clips, nothing reflows). That loses the same
// entries as the pager, without even the pager to hint that something is
// missing.
//
// So a page legend is plain, horizontal and under the plot, it may wrap over
// as many rows as it needs, and the builder RESERVES the height those rows
// take (`grid.bottom`, or the pie's `center`/`radius`) before the plot is
// drawn. Nothing here changes WHICH picture is drawn: same series, same
// families, same order.
//
// Label widths are ESTIMATED, never measured: this module is pure — no DOM, no
// canvas, no `document` (the package's tsconfig has no `DOM` lib), and the
// caller passes the canvas size it measured. The estimate is biased WIDE on
// purpose: over-estimating costs a few pixels of plot height, under-estimating
// costs a legend row off the bottom of a cell that clips.

/** The canvas a page chart is drawn on, in CSS px, as the surface measured it. */
type PageCanvas = { readonly widthPx: number; readonly heightPx: number };

/** Page legend metrics. The first four are ECharts' own defaults for a legend
 *  item (`itemWidth: 25`, the 5px it puts between icon and text, `itemGap: 10`)
 *  plus the 5px padding a legend box carries on each side, so the estimate
 *  below measures the same box ECharts lays out. */
const PAGE_LEGEND_FONT_PX = 11;
/** Average glyph advance at 11px in the report's sans stack. Measured around
 *  5.9px for mixed-case metric names; 6.6 is the deliberate bias wide. */
const PAGE_LEGEND_GLYPH_PX = 6.6;
const PAGE_LEGEND_ICON_PX = 25;
const PAGE_LEGEND_ICON_TEXT_GAP_PX = 5;
const PAGE_LEGEND_ITEM_GAP_PX = 10;
const PAGE_LEGEND_PAD_PX = 10;
/** One legend row: ECharts' `itemHeight` (14) plus the leading between rows. */
const PAGE_LEGEND_LINE_PX = 18;
/** Under the last row: the 4px the legend sits off the bottom edge plus 4px of
 *  air between the plot and the first row. One row therefore costs 26px, which
 *  is exactly what the bottom legend has always been given (`+ 26` below), so
 *  a one-row page legend reserves what today's chart reserves. */
const PAGE_LEGEND_BASE_PX = 8;
/** The narrowest canvas the wrap arithmetic will believe. Below this a "line"
 *  holds nothing and the row count would run away with the entry count. */
const PAGE_LEGEND_MIN_WIDTH_PX = 120;
/** What a page chart assumes when the caller names no canvas. Narrow and short
 *  on purpose: two charts side by side on A4 portrait are ~340px and the
 *  frame's own default canvas is 260px tall, and assuming narrow over-reserves
 *  (a slightly shorter plot) where assuming wide would clip a legend row. */
const PAGE_CANVAS_WIDTH_FALLBACK_PX = 340;
const PAGE_CANVAS_HEIGHT_FALLBACK_PX = 260;

/** A page legend never wraps one name over two rows the way `formatLegendName`
 *  does for the vertical legend: the reserved height is counted in rows, and a
 *  two-row entry would silently cost a row nobody budgeted. A name wider than
 *  the canvas is cut with an ellipsis instead — the entry stays present,
 *  countable and in the same order as its series. */
function truncatePageLegendName(name: string, maxChars: number): string {
  return name.length <= maxChars ? name : `${name.slice(0, Math.max(1, maxChars - 1))}…`;
}

type PageLegendGeometry = {
  /** The `legend` fields a page legend fixes. Each branch adds its own `data`
   *  (and `selected`, where it has one). */
  readonly legend: {
    readonly type: "plain";
    readonly orient: "horizontal";
    readonly bottom: number;
    readonly left: "center";
    readonly textStyle: { readonly fontSize: number };
    readonly formatter: (name: string) => string;
  };
  /** Vertical space to reserve under the plot, in px. */
  readonly heightPx: number;
  /** Rows the entries wrap onto. Zero when there is nothing to draw. */
  readonly lines: number;
};

/**
 * Where a page legend's entries land, and what that costs in height.
 *
 * Greedy wrap over the same box ECharts lays out, at the canvas width the
 * surface measured. Deterministic in its inputs: same names, same width, same
 * answer — which is what lets the server's cell budget and the drawn page
 * agree without either of them measuring the other.
 */
function pageLegendGeometry(names: readonly string[], canvasWidthPx: number): PageLegendGeometry {
  const width = Math.max(PAGE_LEGEND_MIN_WIDTH_PX, Math.floor(canvasWidthPx)) - PAGE_LEGEND_PAD_PX;
  const maxChars = Math.max(
    6,
    Math.floor((width - PAGE_LEGEND_ICON_PX - PAGE_LEGEND_ICON_TEXT_GAP_PX) / PAGE_LEGEND_GLYPH_PX),
  );
  let lines = names.length > 0 ? 1 : 0;
  let used = 0;
  for (const name of names) {
    const chars = Math.min(name.length, maxChars);
    const itemPx =
      PAGE_LEGEND_ICON_PX + PAGE_LEGEND_ICON_TEXT_GAP_PX + Math.ceil(chars * PAGE_LEGEND_GLYPH_PX);
    const needed = used === 0 ? itemPx : used + PAGE_LEGEND_ITEM_GAP_PX + itemPx;
    if (needed > width && used > 0) {
      lines += 1;
      used = itemPx;
    } else {
      used = needed;
    }
  }
  return {
    legend: {
      type: "plain",
      orient: "horizontal",
      bottom: 4,
      left: "center",
      textStyle: { fontSize: PAGE_LEGEND_FONT_PX },
      formatter: (name: string) => truncatePageLegendName(name, maxChars),
    },
    heightPx: PAGE_LEGEND_BASE_PX + lines * PAGE_LEGEND_LINE_PX,
    lines,
  };
}

/**
 * What the plot actually gives up for the legend.
 *
 * A legend may never eat the whole canvas: a grid with no room left draws
 * NOTHING (a negative grid height is a blank chart), and a blank chart is worse
 * than a legend row that sits over the bottom gridline on an unusually short
 * canvas. Nothing is lost by capping: ECharts draws every row of a plain legend
 * whatever the grid reserved, so the entries stay on the page either way — only
 * the promise that they never overlap the plot gives way, and only when the
 * canvas is too short to keep both.
 */
function pageLegendReservePx(geometry: PageLegendGeometry, canvasHeightPx: number): number {
  return Math.min(
    geometry.heightPx,
    Math.max(PAGE_LEGEND_LINE_PX, Math.floor(canvasHeightPx / 2)),
  );
}

/**
 * Truncate or wrap long axis labels for better display.
 * - If label is short enough, return as-is
 * - If label is long, try to split on space/hyphen
 * - If no good split point, truncate with ellipsis
 */
function formatAxisLabel(label: string, maxLength: number = 12): string {
  if (label.length <= maxLength) return label;

  // Try to find a natural break point (space, hyphen, underscore)
  const breakChars = [' ', '-', '_'];
  for (const breakChar of breakChars) {
    const parts = label.split(breakChar);
    if (parts.length >= 2) {
      // Find best split point near middle
      let bestSplit = 0;
      let bestDiff = Infinity;
      let cumLength = 0;
      for (let i = 0; i < parts.length - 1; i++) {
        cumLength += parts[i].length + 1;
        const diff = Math.abs(cumLength - label.length / 2);
        if (diff < bestDiff) {
          bestDiff = diff;
          bestSplit = i;
        }
      }
      const line1 = parts.slice(0, bestSplit + 1).join(breakChar);
      const line2 = parts.slice(bestSplit + 1).join(breakChar);
      // Only wrap if both lines are reasonable length
      if (line1.length <= maxLength + 4 && line2.length <= maxLength + 4) {
        return `${line1}\n${line2}`;
      }
    }
  }

  // No good break point, truncate
  return label.slice(0, maxLength - 1) + '…';
}

/**
 * Calculate optimal x-axis label interval based on data point count.
 * - 0-30 values: show all (interval: 0)
 * - 31-60 values: every 3rd (interval: 2)
 * - 61+ values: every 5th (interval: 4)
 */
function calculateXAxisInterval(dataCount: number): number {
  if (dataCount <= 30) return 0;      // Show all
  if (dataCount <= 60) return 2;      // Every 3rd (0-indexed: 0, 3, 6, ...)
  return 4;                            // Every 5th (0-indexed: 0, 5, 10, ...)
}


/**
 * Calculate optimal left margin based on y-axis label format and values.
 */
function calculateLeftMargin(values: number[], format?: string): number {
  if (values.length === 0) return 24;

  // Use reduce instead of spread to avoid stack overflow with large arrays
  const maxVal = values.reduce((max, v) => {
    const absV = Math.abs(v);
    return absV > max ? absV : max;
  }, 0);
  const sampleLabel = formatYAxisLabel(maxVal, format);

  // Estimate width: ~7px per character
  const estimatedWidth = sampleLabel.length * 7;

  return Math.max(24, Math.min(estimatedWidth, 70));
}

export function chooseBarType(spec: BackendChartSpec): BackendChartSpec["chart_type"] {
  // A stack stays a stack: a stacked bar over time (plan 2026-09-03
  // breakdown, D2) whose toggle goes Line and back must not come back as
  // side-by-side bars. `isStacked` below reads the same substring.
  if (isStackedChartType(spec.chart_type)) {
    // ...but returning the ORIGINAL type only works while every stacked
    // spelling is a bar. `area_stacked` also matches this substring, and
    // handing it back here made the Bar position of the toggle draw the very
    // areas the reader was trying to leave — measured, the toggle was inert.
    // A stacked line-family type answers with the stacked BAR it means.
    return isLineFamily(spec.chart_type) ? "stacked_bar" : spec.chart_type;
  }
  const multiSeries = (spec.y_keys?.length ?? 0) > 1;
  return multiSeries ? "grouped-bar" : "bar";
}

function rowScore(row: Record<string, any>, yKeys: string[]): number {
  if (!Array.isArray(yKeys) || yKeys.length === 0) return 0;
  if (yKeys.length === 1) return toNum(row[yKeys[0]]);
  return yKeys.reduce((sum, k) => sum + toNum(row[k]), 0);
}

/**
 * Keep the N rows that fit on screen and bucket the rest, the way this chart
 * type says it should be done (`CategoryCap`).
 *
 * `cap` is a required parameter rather than a defaulted one. A default here
 * would be the one behaviour that was silently wrong for `waterfall`, and the
 * next caller to forget it would reintroduce exactly that — so the caller has
 * to say which, and the recipe map is where the answer comes from.
 */
export function capCategoriesWithOther(
  spec: BackendChartSpec,
  indexKey: string,
  maxCats: number,
  showOther: boolean,
  cap: CategoryCap,
  otherLabel = "Other"
): BackendChartSpec {
  const rows = spec.rows ?? [];
  const yKeys = spec.y_keys ?? [];

  if (!rows.length || rows.length <= maxCats) return spec;
  // "never" means the caller should not have capped at all; answering with the
  // rows untouched keeps that decision true even if a future caller forgets to
  // ask first.
  if (cap === "never") return spec;
  // An anchored row set is never capped, whatever the recipe says. Capping
  // sums the rows it drops into one "Other" appended at the END, so a trailing
  // end anchor would be added to a pile of deltas and drawn as a step — the
  // exact doubling that marking anchors exists to prevent, and silent. No
  // query emits an anchor today, so this costs nothing and closes the hole
  // rather than documenting it.
  if (rows.some((r) => r?.[WATERFALL_ANCHOR_KEY] === true)) return spec;

  // Ranking by magnitude picks the rows worth showing — except where the row
  // ORDER carries meaning, in which case the first N in the query's own order
  // are the ones to keep and re-ranking would destroy the picture.
  const sorted =
    cap === "first-n-plus-other"
      ? rows
      : [...rows].sort((a, b) => rowScore(b, yKeys) - rowScore(a, yKeys));
  const top = sorted.slice(0, maxCats);
  const rest = sorted.slice(maxCats);

  // Filter comparison_rows to match visible categories (only when it's an array, not a table lookup object)
  const visibleKeys = new Set(top.map((r) => toStr(r[indexKey])));
  let filteredComp = spec.comparison_rows;
  if (Array.isArray(spec.comparison_rows)) {
    filteredComp = spec.comparison_rows.filter((r: any) => visibleKeys.has(toStr(r[indexKey])));
  }

  if (!showOther || rest.length === 0) {
    return { ...spec, rows: top, comparison_rows: filteredComp } as BackendChartSpec;
  }

  const otherRow: Record<PropertyKey, any> = { [indexKey]: otherLabel, [SYNTHETIC_ROW]: true };
  for (const key of yKeys) {
    otherRow[key] = rest.reduce((sum, r) => sum + toNum(r[key]), 0);
  }

  const otherHasValue = yKeys.some((k) => toNum(otherRow[k]) !== 0);
  return { ...spec, rows: otherHasValue ? [...top, otherRow] : top, comparison_rows: filteredComp } as BackendChartSpec;
}

// ------------------------------------------------------------
// ECharts option builder (typed!)
// ------------------------------------------------------------

/**
 * Every series must render against an axis that THIS option defines.
 *
 * ECharts 6 throws on a dangling index (`yAxis "0" not found`,
 * model/referHelper.js) and `EChart` catches that rather than crashing the page
 * — so the failure mode is a blank plot under a fully populated legend, which is
 * indistinguishable from a data problem. That is exactly how horizontal charts
 * with an auto-secondary y-axis rendered empty on prod without anyone noticing.
 *
 * The branches below each build their own axes, so the rule cannot live in one
 * of them. Clamp to axis 0 (a bar on the wrong axis beats no chart at all) and
 * shout, instead of handing ECharts an option it will refuse.
 */
function clampDanglingAxisIndices(option: EChartsOption): EChartsOption {
  const series = (option as any).series;
  if (!Array.isArray(series)) return option;
  const axisCount = (axis: unknown) => (Array.isArray(axis) ? axis.length : axis ? 1 : 0);
  const counts = {
    yAxisIndex: axisCount((option as any).yAxis),
    xAxisIndex: axisCount((option as any).xAxis),
  } as const;

  // Counts and indices only, never series NAMES or data: Sentry's default
  // console breadcrumbs would carry them along, and on a breakdown chart the
  // series names ARE customer values (campaign names, product titles).
  series.forEach((s, i) => {
    if (!s || typeof s !== "object") return;
    for (const prop of ["yAxisIndex", "xAxisIndex"] as const) {
      const idx = (s as any)[prop];
      const count = counts[prop];
      if (typeof idx === "number" && count > 0 && idx >= count) {
        (s as any)[prop] = 0;
        console.error(
          `[chart] series #${i} (type ${(s as any).type ?? "?"}) references ` +
            `${prop} ${idx} but the option defines ${count} axis/axes — clamped to 0.`,
        );
      }
    }
  });
  return option;
}

// ------------------------------------------------------------
// Series builders: how each chart style is BUILT
// ------------------------------------------------------------

/**
 * The series array for one chart type.
 *
 * Everything below the PIE branch of `buildEchartsOptionForSpec` comes out of
 * ONE loop over `spec.y_keys` — one series per metric key, steered by the
 * shape flags (`isArea`, `isStacked`, `isCombo`, `isHorizontal`). `waterfall`
 * is the first type that cannot: it makes TWO series out of ONE key, and no
 * flag on that loop can express that.
 */
type SeriesBuilderInput = {
  rows: Record<string, any>[];
  yKeys: string[];
  labels: Record<string, string> | undefined;
  colors: string[];
  /**
   * A dashboard tile's painter (ADR-0476), or `null` on every surface that
   * passed no `colorLayers`. A builder hands it each item's ref and its
   * positional pick from `colors` (`paintRef`); the painter answers the ref's
   * resolved colour, or the pick when the map has none, and records what it
   * painted for `drawnSeriesColors`. With `null` every item keeps its pick, so
   * the option is what it was before.
   */
  paint: RefPainter | null;
};

/** Paints one item by its series ref: the resolved colour, else `fallback`. */
type RefPainter = (ref: SeriesRef, fallback: string | undefined) => string | undefined;

/** `fallback` without a painter, the painter's answer with one. Typed as
 *  "the resolved colour or exactly what the caller passed". */
function paintRef<T extends string | undefined>(paint: RefPainter | null, ref: SeriesRef, fallback: T): string | T {
  return paint ? (paint(ref, fallback) as string | T) : fallback;
}
type SeriesBuilder = (input: SeriesBuilderInput) => any[];

/**
 * What a WHOLE-OPTION builder gets on top of a series builder's input.
 *
 * A series builder joins the shared cartesian assembly further down — a
 * category x-axis, a value y-axis, `tooltip.trigger: "axis"` — and so needs
 * only its numbers. Three chart types cannot join it, each for a structural
 * reason rather than a stylistic one:
 *
 *  - `scatter` and `bubble` put a METRIC on each axis. Both axes are value
 *    axes and there is no category axis at all, so "one series per y_key with
 *    the dimension along the bottom" is not a smaller version of their picture
 *    — it is a different one.
 *  - `treemap` has no axes whatsoever.
 *
 * `categories` is the formatted dimension value per row, aligned with `rows`
 * by index — the shared assembly's x-axis labels, handed over as data because
 * for these three the dimension names the POINT or the TILE instead of
 * occupying an axis.
 */
type OptionBuilderInput = SeriesBuilderInput & {
  categories: string[];
  /** The SECOND dimension's value per row (`dimension_keys[1]`), aligned with
   *  `rows`; empty when the spec names fewer than two dimensions. Only the
   *  sankey reads it (its flow's "to"); every other builder ignores it. */
  secondCategories: string[];
  /** The spec's metric format, for a builder that prints values itself. */
  formatValue: (value: number) => string;
  /** The y-values are fractions (0-1) that should read as percentages. */
  isPercentage: boolean;
  /** A whole-option builder spreads `chartAnimation` itself, so it needs the
   *  caller's motion decision; a series builder joins the shared assembly,
   *  which spreads it once for everyone. */
  motion: ChartMotion;
  /** The spec's `chart_index_by`: the column that names each row. Only the
   *  funnel reads it, to pair a comparison row with its step by name. */
  indexKey: string;
  /** The comparison window's rows, array form only (a table's keyed lookup
   *  has no meaning here); empty without a comparison. Only the funnel reads
   *  it: the other whole-option pictures draw no comparison. */
  comparisonRows: Record<string, any>[];
  /** What the comparison window is called (`spec.comparison_label`). */
  comparisonLabel: string | undefined;
  /** The spec's `breakdown_key`, when it names one. Only the scatter family
   *  reads it: its rows arrive long-form and it groups them into one series
   *  per value (plan 2026-10-16 seasonal heatmap, D3). */
  breakdownKey: string | undefined;
};
type OptionBuilder = (input: OptionBuilderInput) => EChartsOption;

/**
 * How a chart style is drawn: by contributing SERIES to the shared assembly,
 * or by owning the whole option.
 *
 * A discriminated union rather than a return type the dispatcher sniffs with
 * `Array.isArray`. The two contracts are genuinely different — one may be
 * paired with a comparison overlay, given a secondary axis and folded into the
 * tooltip's index map, the other may not — so which one a builder signed is
 * worth stating rather than inferring from what came back at runtime.
 *
 * `pie`/`donut` are the pre-existing whole-option branch and stay written out
 * inline for now; migrating them onto this union is the follow-up plan named
 * in S12, not a change to make in passing on the evening the union is born.
 */
type ChartBuilder =
  | { kind: "series"; build: SeriesBuilder }
  | { kind: "option"; build: OptionBuilder };

const seriesBuilder = (build: SeriesBuilder): ChartBuilder => ({ kind: "series", build });
const optionBuilder = (build: OptionBuilder): ChartBuilder => ({ kind: "option", build });

/**
 * What happens to a categorical chart's rows once there are more of them than
 * fit on screen (`DEFAULT_MAX_CATEGORIES`).
 *
 * This was one behaviour for every chart type until 2026-09-07, and for two of
 * them it was the wrong one — measured, not suspected:
 *
 *  - a `waterfall` of twelve steps came out as
 *    `start, b, d, f, h, j, k, a, i, c, Other`. The sort is by magnitude, and
 *    a waterfall's SEQUENCE is the statement it makes, which is why
 *    `buildWaterfallSeries` promises in its own docstring never to re-sort.
 *    It does not re-sort; the capping upstream of it does.
 *  - a `scatter`'s "Other" row would be the SUM of the remaining rows' two
 *    metrics — a point at (Σx, Σy) that is not an observation. On a bar chart
 *    a summed remainder is a real quantity; on a plot whose axes are metrics
 *    it is a fabricated data point, which this renderer must never draw.
 */
export type CategoryCap =
  /** Sort by magnitude, keep the top N, sum the rest into one "Other" row. */
  | "top-n-plus-other"
  /** Keep the first N in QUERY order, sum the rest into a final "Other" row. */
  | "first-n-plus-other"
  /**
   * Never cap. The picture gets BETTER with more rows rather than worse, so
   * there is nothing to hide and no remainder to bucket. Implies the higher
   * hard row ceiling (`MAX_CHART_ROWS_LINE`) for the same reason: density is
   * the information.
   */
  | "never";

/**
 * Everything this file needs to know about a chart style, in one place.
 *
 * `build` answers "how is the option made", `cap` answers "what happens when
 * there are too many rows". Both are per chart type, both were previously
 * decided by predicates spread through the file, and both fail SILENTLY when
 * a new type is forgotten — the two properties therefore share one declaration
 * site, whose completeness is a compile error.
 */
type ChartRecipe = {
  /** `null` = the shared `spec.y_keys` loop draws it. */
  build: ChartBuilder | null;
  cap: CategoryCap;
  /** What the style does with `spec.y_keys`; decides which reader controls apply. */
  metrics: MetricChannels;
};

/**
 * What a chart style does with `spec.y_keys`, which decides whether the
 * reader's metric chips and ↓ Best / ↑ Worst switch mean anything for it
 * (plan 2026-10-16-a-chart-offers-only-the-controls-its-picture-reads).
 *
 *  - `series`: one independent series per key; hiding or reordering one is a
 *    legitimate view of the same picture.
 *  - `roles`: each key is an axis or a channel BY POSITION (scatter x/y,
 *    bubble x/y/size, combo's first key as the bar). Filtering or reversing the
 *    keys swaps the picture's roles, so neither control applies.
 *  - `first`: only `y_keys[0]` is drawn; a multi-select over it is a lie.
 *  - `parts`: every key is a part of one sum (plan 2026-10-17 stacked
 *    metrics, D3). All are drawn, in the author's order: no chips, no
 *    ↓ Best / ↑ Worst, and no default cap — a part you cannot see is a
 *    column that no longer adds up to the total it shows.
 */
export type MetricChannels = "series" | "roles" | "first" | "parts";

/**
 * The common case: the shared loop draws it, and top-N + "Other" when crowded.
 *
 * Deliberately WITHOUT `metrics`: every entry of `CHART_RECIPES` spells
 * `{ ...SHARED_LOOP, metrics: … }`, so reusing the constant can never decide
 * what a style's keys are by accident (pie and donut are drawn by this loop
 * and read only their first key). Leaving `metrics` out of an entry is a
 * compile error (R4 code-quality MAJOR 1).
 */
const SHARED_LOOP: Omit<ChartRecipe, "metrics"> = Object.freeze({
  build: null,
  cap: "top-n-plus-other",
});

/** The recipe for a spelling no chart style names: the shared loop, one series per key. */
const FALLBACK_RECIPE: ChartRecipe = Object.freeze({ ...SHARED_LOOP, metrics: "series" });

/**
 * Chart styles this file renders that the registry still has on
 * `status="library"`. A library row is filtered out of `CHART_STYLES` (which
 * derives from `available_on("dashboard")`), so `DashboardChartType` has no
 * member for it yet and `SERIES_BUILDERS` could not name it.
 *
 * Declared as its own `const` tuple and UNIONED into the map's key type rather
 * than widening that key to `string`: a `Record<string, …>` accepts any set of
 * keys, which would give up the one property this map exists to hold. A row
 * that flips to `available` joins `DashboardChartType` by itself and leaves
 * this list — and if someone forgets to remove it, nothing breaks, because the
 * union is idempotent.
 */
export const PREPARED_LIBRARY_CHART_TYPES = ["funnel"] as const;
type PreparedLibraryChartType = (typeof PREPARED_LIBRARY_CHART_TYPES)[number];

/** The invisible series that carries a waterfall's running offsets. */
const WATERFALL_BASE_SERIES_NAME = "_waterfall_base";
/**
 * Both waterfall series sit on ONE stack, named EXPLICITLY.
 *
 * `isStacked` is `chartType.includes("stack")` and "waterfall" does not
 * contain "stack", so without this the base and the bar draw side by side:
 * a wrong picture rather than a broken one, which is the kind nobody reports.
 */
const WATERFALL_STACK = "waterfall";

/**
 * A row key that says "this value is a LEVEL, not a step": draw the bar from
 * zero and SET the running total to it instead of adding to it.
 *
 * Why the renderer needed this (Arjen, 2026-09-19). A waterfall's bars are
 * deltas, and a bridge needs two rows that are not: the level it starts from
 * and the level it ends on. The start one can be faked — a delta from zero
 * draws identically, because the running total starts at zero — and that is
 * exactly what makes faking it wrong: it is a total bar either way, and the
 * only question is whether the drawing SAYS so. The end one cannot be faked at
 * all; added to the running total it lands on twice the end value.
 *
 * This does NOT invent a total. The comment on `buildWaterfallSeries` refuses
 * to synthesise one, and still does: an anchor is a value the caller PUT in
 * the rows, drawn where it says it is. What changed is that a caller now has a
 * way to say it. That comment called the addition "additive and reversible",
 * and this is that addition.
 *
 * Double-underscored because it is the one key in a row that did not come from
 * the query. Mirrored by `services/composition/bridge.py::ANCHOR_KEY`, pinned
 * by a backend test that reads this file.
 */
export const WATERFALL_ANCHOR_KEY = "__anchor";

/**
 * A waterfall: one bar per row, each starting where the previous one ended.
 *
 * ECharts has no waterfall series type. It is two `bar` series on one stack,
 * the first painted transparent and holding the offsets.
 *
 * The offset is `min(before, after)`, NOT the running total. With only
 * positive contributions the two are identical, which is exactly why the naive
 * version survives every happy-path demo; the moment a contribution is
 * negative its bar hangs DOWN from the previous total, so the invisible part
 * beneath it is the LOWER of the two ends. A running total that dips below
 * zero draws correctly under this formulation and under no other.
 *
 * Still no SYNTHETIC total bar, first or last. The canonical ECharts example
 * has one; we deliberately do not — a total is a data point that is not in the
 * query, and this renderer must never invent one. What a caller may do, since
 * 2026-09-19, is SUPPLY one: a row marked `WATERFALL_ANCHOR_KEY` is a level
 * rather than a step, drawn from zero and replacing the running total. That is
 * the "adding a real total later is additive and reversible" this comment
 * promised, and the distinction it was protecting is intact — the number comes
 * from the caller's rows either way, and the renderer computes none of it.
 *
 * A caller whose anchors do not agree with its own steps gets a visible break
 * rather than a quiet one: the end anchor draws at its own value, so a last
 * step that did not land there leaves a step in the outline. Deliberately not
 * corrected here — a renderer that silently reconciles two disagreeing numbers
 * is the one place this bug would become invisible.
 *
 * Row order is the query's order, never re-sorted: the sequence of a waterfall
 * is the statement it makes, and the author controls it through the query.
 */
function buildWaterfallSeries({ rows, yKeys, labels, colors, paint }: SeriesBuilderInput): any[] {
  const key = yKeys[0];
  const values = key == null ? [] : rows.map((r) => toNum(r[key]));
  const anchors = rows.map((r) => r?.[WATERFALL_ANCHOR_KEY] === true);

  const offsets: number[] = [];
  const magnitudes: number[] = [];
  let running = 0;
  for (let i = 0; i < values.length; i++) {
    const v = values[i];
    if (anchors[i]) {
      // A level: it starts at zero and it REPLACES the running total. For the
      // first row the two formulations agree (running is already 0), which is
      // why an unmarked start row draws correctly and an unmarked end row
      // lands on twice the end value.
      offsets.push(Math.min(0, v));
      magnitudes.push(Math.abs(v));
      running = v;
      continue;
    }
    const before = running;
    const after = running + v;
    offsets.push(Math.min(before, after));
    magnitudes.push(Math.abs(v));
    running = after;
  }

  // A step UP and a step DOWN are the whole point of the chart, so they are
  // coloured apart — with the delta colours this file already uses for "went
  // up / went down" rather than a second palette entry, which a reader would
  // take for a second metric.
  // On a dashboard the three are the chart's roles (`r:increase`,
  // `r:decrease`, `r:total`); a role with nothing set keeps its colour here.
  const upColor = paintRef(
    paint,
    "r:increase",
    selectDistributedColors(colors, 1)[0] ?? positionalColor(colors, 0) ?? positionalColor(DEFAULT_CHART_COLORS, 0),
  );
  const downColor = paintRef(paint, "r:decrease", DELTA_NEGATIVE_COLOR);
  const anchorColor = paintRef(paint, "r:total", DELTA_NEUTRAL_COLOR);

  return [
    {
      // `_`-prefixed on purpose: `isHelperSeriesName` keeps every such series
      // out of the legend and out of the tooltip's series lookup, so the
      // invisible offsets never become a legend entry the reader can toggle.
      name: WATERFALL_BASE_SERIES_NAME,
      type: "bar",
      stack: WATERFALL_STACK,
      yAxisIndex: 0,
      barMaxWidth: 50,
      silent: true,
      legendHoverLink: false,
      itemStyle: { color: "transparent" },
      emphasis: { disabled: true },
      tooltip: { show: false },
      data: offsets,
      z: 0,
    },
    {
      name: labels?.[key] ?? key ?? "",
      type: "bar",
      stack: WATERFALL_STACK,
      yAxisIndex: 0,
      barMaxWidth: 50,
      emphasis: { focus: "series" as const },
      data: magnitudes.map((magnitude, i) => ({
        value: magnitude,
        itemStyle: {
          // An anchor is neither a rise nor a fall, so it gets the colour this
          // file already means by "neither": painting it as an up bar would
          // read as a step the size of the whole total.
          color: barGradient(
            anchors[i] ? anchorColor : values[i] < 0 ? downColor : upColor,
            false,
          ),
          borderRadius: [3, 3, 3, 3],
        },
      })),
      z: 1,
    },
  ];
}

/**
 * Each row's components divided by that row's total, so every column of a
 * `bar_stacked_100` fills the same height and the reader compares COMPOSITION
 * instead of size.
 *
 * Fractions (0-1), never 0-100: `isPercentageData` recognises -1..1, and
 * `isAlreadyPercentageData` additionally demands a %-shaped key name — so
 * emitting fractions makes the existing percentage formatting (axis labels,
 * tooltip) apply itself, where 0-100 would be drawn as raw counts.
 *
 * A row draws EMPTY in two cases, and both are a decision rather than a
 * fallback:
 *  - its components total zero, or less. Division by zero has to be
 *    IMPOSSIBLE here, not merely unlikely — the guard is on the divisor, not
 *    on the quotient, so no `NaN` and no `Infinity` can be constructed.
 *  - any single component is negative. A percentage of a mixed-sign total is
 *    undefined, and normalising `abs()` values would report a share the data
 *    does not contain. Empty is the honest rendering and the same treatment as
 *    a zero total: that column is absent, every other column still reads.
 *
 * "Empty" is written here as `null` per component, which the shared series
 * loop then coerces to a zero-height column (`toNum(null) === 0`). Absent on
 * screen either way; the `null` is what says at the source that the share is
 * undefined rather than measured, so preserving it downstream would be an
 * improvement rather than a change of meaning.
 *
 * EXPORTED for its own unit test, and that is not a convenience. `toNum`
 * turns both `NaN` and `Infinity` into `0` on the way into the series, so a
 * zero-total row renders identically whether the guard above is present or
 * absent — measured: deleting it reddens nothing at the option level. The
 * divide-by-zero refusal therefore has no falsifier unless it is asserted
 * where it is made.
 */
export function normalizeRowsTo100Percent(
  rows: Record<string, any>[],
  yKeys: string[],
): Record<string, any>[] {
  if (yKeys.length === 0) return rows;
  return rows.map((row) => {
    const values = yKeys.map((k) => toNum(row[k]));
    const total = values.reduce((sum, v) => sum + v, 0);
    // `total > 0` refuses a zero total and a negative one at once; the `every`
    // refuses the third case a positive total hides — one negative component
    // among larger positive ones.
    const drawable = total > 0 && values.every((v) => v >= 0);
    const out: Record<string, any> = { ...row };
    yKeys.forEach((k, i) => {
      out[k] = drawable ? values[i] / total : null;
    });
    return out;
  });
}

/**
 * The number format of a stacked metrics chart's parts and total.
 *
 * `_100`: the share, always — the rows are fractions by then, whatever unit
 * the parts were measured in. Absolute: the first part's catalog format, which
 * the chart branch stamps per key in `series_config` (all parts share it: the
 * write rule refuses two different known formats among them), falling back
 * to `metric_format`. Never READ `metric_format` ahead of `series_config`,
 * and never stamp it on a parts spec: `primaryFormat` takes it ahead of the
 * fraction detection, so a `_100` axis would print 0.32 as a currency.
 */
function partsTotalFormat(spec: BackendChartSpec, yKeys: string[], hundredPercent: boolean): string | undefined {
  if (hundredPercent) return "fraction";
  const configs = spec.series_config ?? [];
  const first = configs.find((c) => c.key === yKeys[0])?.format ?? configs.find((c) => c.format)?.format;
  return first ?? ((spec as any).metric_format as string | undefined) ?? undefined;
}

/**
 * Per row: the sum of the POSITIVE measured parts — where the top of the
 * column is, since ECharts stacks a negative part downward from zero — and
 * the NET sum of every measured part, which is the total the column stands
 * for. A null part is a hole, never a zero; a row with no measured part has
 * no total (`null`).
 */
export function partsStackTotals(
  rows: Record<string, any>[],
  yKeys: string[],
): Array<{ top: number | null; net: number | null }> {
  return rows.map((row) => {
    let top = 0;
    let net = 0;
    let measured = 0;
    for (const k of yKeys) {
      const v = measuredNumber(row[k]);
      if (v == null) continue;
      measured++;
      net += v;
      if (v > 0) top += v;
    }
    return measured === 0 ? { top: null, net: null } : { top, net };
  });
}

/**
 * The invisible series that carries the total label of a stacked metrics
 * chart: one point per period at the column's top, labelled with the net
 * total. A scatter of 1px transparent points rather than a symbol-less line:
 * ECharts draws a line's labels on its symbols, so `symbol: "none"` would
 * draw no label at all, and a line thins its symbols to the axis label
 * interval. Not on the stack, `silent`, no tooltip.
 *
 * The formatter returns the formatted NUMBER only. An ECharts label formatter
 * is parsed for `{style|…}` rich text, so interpolating a series or metric
 * name (author-written text) here would be markup; a formatted number holds
 * no braces.
 */
function partsTotalLabelSeries(
  rows: Record<string, any>[],
  yKeys: string[],
  format: string | undefined,
): any {
  const totals = partsStackTotals(rows, yKeys);
  return {
    name: PARTS_TOTAL_LABEL_SERIES_NAME,
    type: "scatter",
    symbol: "circle",
    symbolSize: 1,
    silent: true,
    legendHoverLink: false,
    tooltip: { show: false },
    emphasis: { disabled: true },
    itemStyle: { color: "transparent" },
    z: 4,
    label: {
      show: true,
      position: "top",
      distance: 4,
      // The axis labels' colour (`axisLabel.color` below): the builder has no
      // theme input, and this neutral is what the axis furniture already
      // relies on to read on a light and a dark tile alike.
      color: AXIS_LABEL_COLOR,
      fontSize: 11,
      // The y-axis's compact format (`€501K`, `€1.4M`), not the tooltip's
      // cents: a row of full amounts collides with its neighbours at tile
      // width, and the label is read against the axis (browser gate S8).
      formatter: (p: { dataIndex: number }) => {
        const net = totals[p.dataIndex]?.net;
        return net == null ? "" : formatYAxisLabel(net, format);
      },
    },
    data: totals.map((t) => t.top),
  };
}

/**
 * A positional pick handed to code typed `string` (a gradient, a lightened
 * overlay, a hex wash). An empty palette yields `undefined` here, exactly as
 * the inline `palette[i % n]` these sites used to hold did, and it reaches
 * the same place it always reached; the cast says so at the call instead of
 * hiding it in the picker's return type.
 */
const asDrawnColor = (color: string | undefined): string => color as string;

/** A palette is only useful if it has entries; an empty one falls back. */
const chartColorsOrDefault = (colors: string[]) =>
  colors.length > 0 ? colors : DEFAULT_CHART_COLORS;

/**
 * A point on a scatter is a plain dot; a bubble's dot is sized by a third
 * metric. Between these two the dot has to stay clickable at the bottom end
 * and stay inside its own tile at the top.
 */
const SCATTER_SYMBOL_SIZE = 12;
const BUBBLE_MIN_SYMBOL_SIZE = 8;
const BUBBLE_MAX_SYMBOL_SIZE = 46;
/**
 * Points overlap — that is what a scatter is FOR — so they are drawn
 * translucent. Two coincident opaque dots are indistinguishable from one.
 */
const SCATTER_SYMBOL_OPACITY = 0.75;

type ScatterPoint = { name: string; value: number[] };
type ScatterGroup = { name: string; color: string | undefined; points: ScatterPoint[] };

/**
 * Long-form points grouped into one series per breakdown value
 * (`groupScatterSeries`, shared with `seriesRefsOf`), each with its colour.
 * Without colour layers the values are coloured from `palette` in group
 * order, "(not set)" in its fixed grey, as they always were; on a dashboard
 * each group takes its ref's resolved colour.
 */
function splitScatterSeries(
  placed: { raw: unknown; item: ScatterPoint }[],
  palette: string[],
  paint: RefPainter | null,
): { groups: ScatterGroup[]; dropped: number } {
  const { groups, dropped } = groupScatterSeries(placed);
  let colorIndex = 0;
  return {
    groups: groups.map((g) => {
      const legacy = g.notSet ? SCATTER_NOT_SET_COLOR : positionalColor(palette, colorIndex++);
      return { name: g.name, color: paintRef(paint, scatterGroupRef(g), legacy), points: g.items };
    }),
    dropped,
  };
}

/** What the chart says under itself when the cap dropped series. */
function scatterSeriesNote(dropped: number): string {
  return `${dropped} more series not shown`;
}

/**
 * `scatter` and `bubble`: two metrics as the axes, one point per dimension
 * value, and for a bubble a third metric as the point's area.
 *
 * One function for both, because a bubble IS a scatter with a size channel —
 * the registry says so too (both rows are `family="scatter"`). Splitting them
 * would duplicate the axes, the tooltip and the point construction so that
 * three lines could differ.
 *
 * Rows that cannot be PLACED are left out, and that is the one decision here
 * that is not cosmetic. Everywhere else in this file a missing value goes
 * through `toNum`, which turns `null`, `""` and `NaN` into `0`; on a bar chart
 * that is a zero-height bar, which is approximately honest. On a plot whose
 * axes are both metrics it is a point at the ORIGIN that is not in the data —
 * a fabricated observation sitting in the corner of a correlation the reader
 * is trying to judge. So a row whose x or y is not a finite number is dropped
 * rather than coerced.
 *
 * A missing SIZE is different again: the point's position is still known, and
 * position is the primary information. It draws at the minimum size, which is
 * also where a genuine zero and a genuine negative land — an area cannot be
 * negative, and the size channel cannot resolve below its own floor anyway.
 */
function buildScatterFamilyOption(
  { rows, yKeys, labels, colors, paint, categories, isPercentage, motion, breakdownKey }: OptionBuilderInput,
  sized: boolean,
): EChartsOption {
  const [xMetric, yMetric, sizeMetric] = yKeys;
  const nameOf = (key: string | undefined) => (key ? labels?.[key] ?? key : "");
  const format = (value: number) =>
    isPercentage ? formatPercentage(value) : formatTooltipValue(value);

  // Each placed point keeps its row's breakdown value beside it, for the split
  // below; the point object itself is what it always was. The placement is
  // `seriesRefsOf`'s too, so both count the same rows per series.
  const placed = placeScatterRows(rows, yKeys, breakdownKey, (row, i, xValue, yValue): ScatterPoint => {
    const rawSize = sized && sizeMetric != null ? measuredNumber(row[sizeMetric]) : 0;
    const size = Math.max(0, rawSize ?? 0);
    return {
      name: categories[i] ?? "",
      value: sized ? [xValue, yValue, size] : [xValue, yValue],
    };
  });
  const points = placed.map((p) => p.item);
  // With a breakdown (plan 2026-10-16 seasonal heatmap, D3): one series per
  // value. Without one, the option below is exactly what it always was.
  const split = breakdownKey ? splitScatterSeries(placed, chartColorsOrDefault(colors), paint) : null;
  // Without a breakdown the one series is the chart's measure, named by its
  // first key (`seriesRefsOf`: `m:<yKeys[0]>`). On a dashboard it carries its
  // colour explicitly (the palette's first entry, what `option.color` gives
  // it, when nothing is set); elsewhere `option.color` paints it as before.
  const singleColor =
    split || !yKeys[0] || !paint
      ? undefined
      : paintRef(paint, `m:${yKeys[0]}`, positionalColor(chartColorsOrDefault(colors), 0));

  // Over ALL points, every series: a per-series scale would draw each
  // series' largest bubble at full size, whatever its value.
  const largestSize = points.reduce(
    (max, p) => Math.max(max, (p.value as number[])[2] ?? 0),
    0,
  );

  /**
   * AREA proportional to the value, so the radius grows with its square root.
   * Scaling the diameter linearly instead is the classic bubble-chart lie: it
   * makes a value twice as large cover four times the ink.
   */
  const symbolSize = (value: any) => {
    if (!sized) return SCATTER_SYMBOL_SIZE;
    if (largestSize <= 0) return BUBBLE_MIN_SYMBOL_SIZE;
    const raw = Array.isArray(value) ? Number(value[2]) : NaN;
    const share = Number.isFinite(raw) ? Math.max(0, raw) / largestSize : 0;
    return BUBBLE_MIN_SYMBOL_SIZE + (BUBBLE_MAX_SYMBOL_SIZE - BUBBLE_MIN_SYMBOL_SIZE) * Math.sqrt(share);
  };

  const axisLabel = {
    formatter: (value: number) =>
      isPercentage ? formatPercentage(Number(value)) : String(smartNumber(Number(value), 1)),
    fontSize: 11,
    color: "#666",
  };

  return {
    ...chartAnimation("scatter", motion),
    color: chartColorsOrDefault(colors),
    grid: split
      ? { left: 12, right: 20, top: 40, bottom: split.dropped > 0 ? 52 : 32, containLabel: true }
      : { left: 12, right: 20, top: 16, bottom: 32, containLabel: true },
    xAxis: {
      type: "value",
      // Both axes carry a metric, and which metric is on which axis is not
      // recoverable from the picture. An unnamed scatter axis is a chart the
      // reader cannot read at all, so the names are not decoration here.
      name: nameOf(xMetric),
      nameLocation: "middle",
      nameGap: 28,
      nameTextStyle: { fontSize: 11, color: "#666" },
      axisLabel,
      splitLine: { lineStyle: { color: "#f0f0f0" } },
    },
    yAxis: {
      type: "value",
      name: nameOf(yMetric),
      nameLocation: "middle",
      // ⚠️ KNOWN DEFECT, measured at the browser gate 2026-09-08 and NOT fixed
      // here: in the browser this name renders as stacked single characters
      // instead of a rotated word — an unreadable smear over the tick labels.
      // `nameRotate: 90` and `nameTextStyle.overflow: "none"` were both tried
      // and neither changes the canvas render, while an edit to the x-axis
      // name in this same object DOES appear (discriminator run, so the branch
      // is live and the option is used). SSR is not affected: the golden SVG
      // already carries the rotation matrix, which is why the picture guard
      // cannot see this at all. Owner: the follow-up plan named in the S7
      // record; do not "fix" it with these two properties again.
      nameGap: 46,
      nameTextStyle: { fontSize: 11, color: "#666" },
      axisLabel,
      splitLine: { lineStyle: { color: "#f0f0f0" } },
    },
    tooltip: {
      // `item`, not `axis`: on a value/value plot an axis pointer would gather
      // every point sharing an x, which for continuous metrics is normally
      // exactly one and occasionally an arbitrary crowd.
      trigger: "item",
      confine: true,
      textStyle: { fontSize: TOOLTIP_FONT_SIZE },
      formatter: (params: any) => {
        const value = (params?.value ?? []) as number[];
        // Every interpolated string here is customer data — the dimension
        // value and the metric labels both come from the warehouse — and an
        // ECharts tooltip is rendered as HTML.
        // The series name is a breakdown VALUE — customer data too.
        const lines = [
          `<strong>${escapeHtml(String(params?.name ?? ""))}</strong>`,
          ...(split ? [escapeHtml(String(params?.seriesName ?? ""))] : []),
          `${escapeHtml(nameOf(xMetric))}: ${format(Number(value[0]))}`,
          `${escapeHtml(nameOf(yMetric))}: ${format(Number(value[1]))}`,
        ];
        if (sized) lines.push(`${escapeHtml(nameOf(sizeMetric))}: ${format(Number(value[2]))}`);
        return lines.join("<br/>");
      },
    },
    series: split
      ? split.groups.map((g) => ({
          type: "scatter",
          name: g.name,
          symbolSize,
          itemStyle: { opacity: SCATTER_SYMBOL_OPACITY, color: g.color },
          emphasis: { focus: "series" as const, itemStyle: { opacity: 1 } },
          data: g.points,
        }))
      : [
          {
            type: "scatter",
            symbolSize,
            itemStyle: singleColor
              ? { opacity: SCATTER_SYMBOL_OPACITY, color: singleColor }
              : { opacity: SCATTER_SYMBOL_OPACITY },
            emphasis: { focus: "series" as const, itemStyle: { opacity: 1 } },
            data: points,
          },
        ],
    ...(split
      ? {
          legend: {
            type: "scroll",
            top: 0,
            left: "center",
            data: split.groups.map((g) => g.name),
            textStyle: { fontSize: 11 },
          },
          // A `title`, like the treemap's refusal: `TitleComponent` is in
          // every surface's `use()` list, a `graphic` is not.
          ...(split.dropped > 0
            ? {
                title: {
                  text: scatterSeriesNote(split.dropped),
                  right: 8,
                  bottom: 0,
                  textStyle: { fontSize: 11, fontWeight: "normal" as const, color: "#94a3b8" },
                },
              }
            : {}),
        }
      : {}),
  } as EChartsOption;
}

function buildScatterOption(input: OptionBuilderInput): EChartsOption {
  return buildScatterFamilyOption(input, false);
}

function buildBubbleOption(input: OptionBuilderInput): EChartsOption {
  return buildScatterFamilyOption(input, true);
}

/**
 * `treemap`: one metric split by one dimension, as tiles whose AREA is each
 * value's share of the whole.
 *
 * The whole is the point, which is why the registry row takes exactly one
 * metric — and why a negative value has no rendering here. The refusal is the
 * same predicate, for the same reason, as `normalizeRowsTo100Percent`'s: a
 * share of a mixed-sign total is undefined, and an area cannot be negative.
 * Where a 100% stack can localise that refusal to one column and leave the
 * rest of the chart readable, a treemap cannot — every tile is sized against
 * the same total — so the refusal is the whole picture.
 *
 * Zero is NOT refused. A category that contributed nothing has a defined
 * share, and a tile with no area is the correct drawing of it.
 *
 * **The refusal now says why.** Until the browser gate on 2026-09-08 it drew
 * literally nothing — a titled white box with no message, which is the one
 * failure mode this whole route exists to prevent: a tile that looks broken
 * and explains nothing. Measured at the gate with one negative value among
 * five positives; Arjen: "kunnen we dit niet meteen fixen?".
 *
 * The message is a `title`, deliberately, and not a `graphic`: `TitleComponent`
 * is already in the sandbox's `use()` list while `GraphicComponent` is not, so
 * this costs zero extra bytes in the report iframe and needs no change to that
 * registry (plan one-drawing-layer, A5). A component missing from that list
 * draws NOTHING and reports no error, which would have reproduced the exact
 * bug this fixes.
 */
function buildTreemapOption({
  rows,
  yKeys,
  labels,
  colors,
  paint,
  categories,
  isPercentage,
  motion,
  indexKey,
}: OptionBuilderInput): EChartsOption {
  const key = yKeys[0];
  const values = key == null ? [] : rows.map((r) => toNum(r[key]));
  const total = values.reduce((sum, v) => sum + v, 0);
  const negatives = values.filter((v) => v < 0).length;
  const drawable = key != null && total > 0 && negatives === 0;
  // On a dashboard each tile carries its value's resolved colour: a treemap
  // paints a tile from `option.color` by position otherwise, which is exactly
  // the colour-by-rank ADR-0476 replaces.
  const data = drawable
    ? rows.map((row, i) => {
        const tileColor = paintRef(paint, dimensionValueRef(row[indexKey]), undefined);
        return tileColor
          ? { name: categories[i] ?? "", value: values[i], itemStyle: { color: tileColor } }
          : { name: categories[i] ?? "", value: values[i] };
      })
    : [];
  const metricLabel = key ? labels?.[key] ?? key : "";

  // Why this picture cannot be drawn, in the reader's terms. The two reasons
  // are genuinely different and a single "no data" would be wrong for both:
  // negative values are a shape a treemap cannot express, an all-zero total is
  // simply nothing to divide up. Customer-facing English, one line each.
  const refusal = drawable
    ? null
    : negatives > 0
      ? {
          text: negatives === 1 ? "One category has a negative value" : `${negatives} categories have a negative value`,
          subtext: "A treemap sizes each tile by its share of the total, so every value has to be zero or higher.",
        }
      : { text: "Nothing to show", subtext: "Every category in this period is zero." };

  return {
    ...chartAnimation("treemap", motion),
    ...(refusal
      ? {
          title: {
            text: refusal.text,
            subtext: refusal.subtext,
            left: "center",
            top: "middle",
            textAlign: "center",
            // The same neutral the axis furniture in this file uses, so it
            // reads on either theme without the builder knowing which one.
            textStyle: { fontSize: 13, fontWeight: "normal" as const, color: "#666" },
            subtextStyle: { fontSize: 11, color: "#94a3b8", width: 320, overflow: "break" as const },
          },
        }
      : {}),
    color: selectDistributedColors(chartColorsOrDefault(colors), data.length),
    tooltip: {
      trigger: "item",
      confine: true,
      textStyle: { fontSize: TOOLTIP_FONT_SIZE },
      formatter: (params: any) => {
        const value = Number(params?.value ?? 0);
        const share = total > 0 ? `${((value / total) * 100).toFixed(1)}%` : "";
        // Rendered as HTML, and both the tile name and the metric label are
        // customer data.
        return [
          `<strong>${escapeHtml(String(params?.name ?? ""))}</strong>`,
          `${escapeHtml(metricLabel)}: ${isPercentage ? formatPercentage(value) : formatTooltipValue(value)}` +
            (share ? ` (${share})` : ""),
        ].join("<br/>");
      },
    },
    series: [
      {
        type: "treemap",
        // One level of tiles, so there is nothing to zoom into and nothing to
        // navigate back from. Left on, a click would replace the chart with a
        // single tile and offer a breadcrumb to undo it.
        roam: false,
        nodeClick: false,
        breadcrumb: { show: false },
        left: 0,
        right: 0,
        top: 0,
        bottom: 0,
        width: "100%",
        height: "100%",
        itemStyle: { borderColor: "#fff", borderWidth: 2, gapWidth: 2 },
        // A LABEL is drawn as text rather than HTML, so it is not escaped —
        // escaping here would show a literal `&amp;` to the reader. The
        // tooltip above is the one that goes through innerHTML.
        label: { show: true, fontSize: 11, overflow: "truncate", color: "#fff" },
        data,
      },
    ],
  } as EChartsOption;
}

/** What a ratio prints when its divisor is zero or missing: never a confident
 *  0% or a division by zero rendered as "Infinity%". */
const NO_RATIO = "—";

/** `part / whole` as a percentage, or `NO_RATIO` when it is undefined. */
function funnelRatio(part: number | null, whole: number | null): string {
  if (part == null || whole == null || whole === 0) return NO_RATIO;
  return `${((part / whole) * 100).toFixed(1)}%`;
}

/**
 * `funnel`: ordered steps, each drawn as a bar whose WIDTH is its value
 * against the widest step, stacked top to bottom in ROW order.
 *
 * Row order is step order, and that is a contract with the producer, not a
 * sort this builder chooses: the backend emits a metric funnel's rows in
 * `ir.metrics` order and a dimension funnel's rows in the query's (or the
 * author's) order. So the series says `sort: "none"`, and the recipe says
 * `cap: "never"`: the shared top-N cap would re-sort the steps by magnitude
 * and sum the tail into "Other", which destroys the sequence that IS the
 * chart.
 *
 * Honesty rules, each of which a quieter picture would break:
 *  - the scale is the LARGEST step, not the first. A step above its
 *    predecessor draws wider than it, and "% of previous" reads above 100%.
 *    No clamping; a funnel that widens is information.
 *  - a NULL step is a hole, not a zero: drawn without area and labelled
 *    with `NO_RATIO`. Both ratios print `NO_RATIO` when their divisor is
 *    zero or NULL.
 *  - a comparison window pairs with a step by its NAME. For a metric funnel
 *    that is the same as by position, because both windows are produced from
 *    the same `ir.metrics` and labels (and step names are distinct by the
 *    write rule); for a dimension funnel it is the only correct pairing,
 *    because the other window can hold other values. A step with no partner
 *    draws no ghost.
 *
 * The tooltip goes through `innerHTML`, so every interpolated string is
 * escaped there. The data labels are canvas text and are not.
 */
function buildFunnelOption({
  rows,
  yKeys,
  labels,
  colors,
  paint,
  categories,
  formatValue,
  isPercentage,
  motion,
  indexKey,
  comparisonRows,
  comparisonLabel,
}: OptionBuilderInput): EChartsOption {
  const key = yKeys[0];
  // The steps, the comparison paired by name and the scale: `funnelSteps`,
  // which `seriesRefsOf` reads too, so a refused funnel lists no step.
  const { values, comparison, scale } = funnelSteps(rows, key, comparisonRows, indexKey);
  const names = rows.map((_, i) => categories[i] ?? "");
  const format = (v: number) => (isPercentage ? formatPercentage(v) : formatValue(v));
  const hasComparison = comparison.some((v) => v != null);

  const first = values[0] ?? null;
  const palette = chartColorsOrDefault(colors);
  const label = comparisonLabel || "Previous period";

  if (scale <= 0) {
    return {
      ...chartAnimation("funnel", motion),
      title: {
        text: "Nothing to show",
        subtext: rows.length === 0 ? "There are no steps in this period." : "Every step in this period is zero or empty.",
        left: "center",
        top: "middle",
        textAlign: "center",
        textStyle: { fontSize: 13, fontWeight: "normal" as const, color: "#666" },
        subtextStyle: { fontSize: 11, color: "#94a3b8", width: 320, overflow: "break" as const },
      },
      series: [],
    } as EChartsOption;
  }

  const geometry = {
    type: "funnel" as const,
    sort: "none" as const,
    funnelAlign: "center" as const,
    min: 0,
    max: scale,
    minSize: "0%",
    maxSize: "100%",
    gap: 2,
    left: "4%",
    right: "38%",
    top: 8,
    bottom: 8,
  };

  return {
    ...chartAnimation("funnel", motion),
    tooltip: {
      trigger: "item",
      confine: true,
      textStyle: { fontSize: TOOLTIP_FONT_SIZE },
      formatter: (params: any) => {
        const i = Number(params?.dataIndex ?? -1);
        if (params?.seriesIndex !== 0 || i < 0 || i >= rows.length) return "";
        const value = values[i];
        const lines = [
          `<strong>${escapeHtml(names[i])}</strong>`,
          `Value: ${value == null ? NO_RATIO : format(value)}`,
          `% of first step: ${funnelRatio(value, first)}`,
        ];
        if (i > 0) lines.push(`% of previous step: ${funnelRatio(value, values[i - 1])}`);
        const previous = comparison[i];
        if (previous != null) {
          const delta = funnelRatio(value == null ? null : value - previous, previous);
          const signed = delta !== NO_RATIO && !delta.startsWith("-") ? `+${delta}` : delta;
          lines.push(`${escapeHtml(label)}: ${format(previous)} (${signed})`);
        }
        return lines.join("<br/>");
      },
    },
    series: [
      {
        ...geometry,
        name: key ?? "",
        z: 2,
        label: {
          show: true,
          position: "right",
          fontSize: 11,
          color: "#444",
          // Canvas text, not HTML: escaping here would print a literal `&amp;`.
          formatter: (params: any) => {
            const i = Number(params?.dataIndex ?? 0);
            const value = values[i];
            const shown = value == null ? NO_RATIO : format(value);
            return i === 0 ? `${names[i]}\n${shown}` : `${names[i]}\n${shown} · ${funnelRatio(value, first)}`;
          },
        },
        labelLine: { show: true, length: 12, lineStyle: { color: "#cbd5e1" } },
        itemStyle: { borderColor: "#fff", borderWidth: 1 },
        data: rows.map((row, i) => {
          // A step's ref is its metric (a metric funnel) or its value (a
          // dimension funnel), `funnelStepRef`; nothing resolved, by position.
          const ref = paint ? funnelStepRef(labels, indexKey, row) : null;
          const pick = positionalColor(palette, i);
          return {
            name: names[i],
            value: values[i] ?? 0,
            itemStyle: { color: ref === null ? pick : paintRef(paint, ref, pick) },
          };
        }),
      },
      ...(hasComparison
        ? [
            {
              ...geometry,
              name: label,
              // Above the steps and unfilled, so the outline reads whether the
              // previous window was wider or narrower than this one.
              z: 3,
              silent: true,
              label: { show: false },
              labelLine: { show: false },
              emphasis: { disabled: true },
              data: rows.map((_, i) => ({
                name: names[i],
                value: comparison[i] ?? 0,
                itemStyle: {
                  color: "transparent",
                  borderColor: comparison[i] == null ? "transparent" : "#64748b",
                  borderWidth: comparison[i] == null ? 0 : 1.5,
                  borderType: "dashed" as const,
                },
              })),
            },
          ]
        : []),
    ],
  } as EChartsOption;
}

/**
 * How every offerable chart style is BUILT and CAPPED — declared, not discovered.
 *
 * `SHARED_LOOP` is not a gap. It states that the shared `y_keys` loop already
 * draws this style and that the ordinary top-N + "Other" capping suits it,
 * which is true for twelve of the sixteen entries and is meant to stay true:
 * migrating them onto builders is out of scope here and has its own plan. What
 * the map buys is that the declaration cannot be SKIPPED — the key type is
 * `DashboardChartType | PreparedLibraryChartType`, and `DashboardChartType` is
 * `(typeof CHART_STYLES)[number]` over a `const` tuple, so a chart style added
 * to `CHART_STYLES` without an entry here is a COMPILE error rather than a
 * chart that quietly falls through to bars. That fall-through is not
 * hypothetical: `area`, `donut`, `heatmap` and `scatter` each sat in the
 * accepted set drawing bars, and none of them failed anything.
 *
 * The capping question joined this map rather than getting a list of its own
 * for the same reason, and it arrived carrying a live defect: `waterfall` was
 * being re-sorted by magnitude above ten rows (see `CategoryCap`).
 */
const CHART_RECIPES: Record<DashboardChartType | PreparedLibraryChartType, ChartRecipe> = {
  line: { ...SHARED_LOOP, metrics: "series" },
  area: { ...SHARED_LOOP, metrics: "series" },
  line_smoothed: { ...SHARED_LOOP, metrics: "series" },
  area_stacked: { ...SHARED_LOOP, metrics: "series" },
  combo: { ...SHARED_LOOP, metrics: "roles" },
  bar: { ...SHARED_LOOP, metrics: "series" },
  horizontal_bar: { ...SHARED_LOOP, metrics: "series" },
  stacked_bar: { ...SHARED_LOOP, metrics: "series" },
  "grouped-bar": { ...SHARED_LOOP, metrics: "series" },
  pie: { ...SHARED_LOOP, metrics: "first" },
  donut: { ...SHARED_LOOP, metrics: "first" },
  // The shared stacked loop draws it. The "100%" part is a ROW NORMALISATION
  // applied to the spec before that loop runs (`normalizeRowsTo100Percent`),
  // not a different series shape — so `build` is honestly `null`.
  bar_stacked_100: { ...SHARED_LOOP, metrics: "series" },
  // The parts of one total, stacked per period (plan 2026-10-17 stacked
  // metrics). The shared stacked loop draws them; the total label and the
  // tooltip's "Total" line are added by the builder for the `parts` channel.
  // `cap: "never"` is DECLARED, not inherited: these rows are time series,
  // and a time series is never category-capped (`tooManyCats` needs
  // `isCategorical`) — "Other" over periods would be a fabricated period.
  // `_100` is the same loop over rows rewritten to shares, like
  // `bar_stacked_100` (`HUNDRED_PERCENT_STACK_CHART_TYPES`).
  bar_stacked_metrics: { ...SHARED_LOOP, cap: "never", metrics: "parts" },
  bar_stacked_metrics_100: { ...SHARED_LOOP, cap: "never", metrics: "parts" },
  waterfall: { build: seriesBuilder(buildWaterfallSeries), cap: "first-n-plus-other", metrics: "first" },
  // `never`, where the waterfall next door caps at first-N. Not a copy with a
  // different word: a bridge's rows ALREADY are the capped picture. The
  // backend ranked the members, cut them at `TOP_K` and put the remainder in
  // its own "Other" bar computed from the headline totals — so a second cap
  // here would append a second "Other" AFTER the closing anchor and break the
  // one property the chart exists to have, that the bars add up.
  //
  // ⚠️ This word is a STATEMENT OF INTENT and no test can tell it from
  // `"first-n-plus-other"`. Measured, not assumed (W5.4 falsification, N5):
  // swapping it leaves every suite green, because `capCategoriesWithOther`
  // already returns an anchored row set untouched, and a bridge's rows always
  // carry anchors. Said out loud so the next reader does not take the word for
  // a tested behaviour — the protection here is the anchor guard, and this
  // declaration only records what the chart would want if that guard were
  // ever removed.
  bridge: { build: seriesBuilder(buildBridgeSeries), cap: "never", metrics: "first" },
  // `never`: the server built these rows in the author's order, and every
  // level is a query value; a fold into "Other" would sum levels and steps.
  metric_waterfall: { build: seriesBuilder(buildStepWaterfallSeries), cap: "never", metrics: "first" },
  // A plot whose axes are metrics has no remainder to bucket: see `CategoryCap`.
  scatter: { build: optionBuilder(buildScatterOption), cap: "never", metrics: "roles" },
  bubble: { build: optionBuilder(buildBubbleOption), cap: "never", metrics: "roles" },
  // A treemap DOES sum its parts, so an "Other" tile is a real quantity and
  // the ordinary capping is right for it.
  treemap: { build: optionBuilder(buildTreemapOption), cap: "top-n-plus-other", metrics: "first" },
  // `never` here and a fold INSIDE the builder: the shared cap ranks ROWS,
  // which for a sankey are edges, and builds its "Other" row with only the
  // index key set, an edge "Other -> undefined". The builder keeps the top
  // nodes per side instead and folds the rest into a per-side "Other"; flows
  // sum, so that is honest (plan 2026-10-05 finance chart types, C3).
  sankey: {
    build: optionBuilder((input) => {
      // The node colours by position, as they always were; the one-colour
      // fallback is what a sankey drew with an empty palette.
      const palette = input.colors.length > 0 ? input.colors : ["#5470c6"];
      return buildSankeyOption({
        ...input,
        // Identity by the raw value, the formatted one for the label only
        // (step 6 review M1): a date-valued node must key like the pane.
        categories: input.rows.map((r) => toStr(r[input.indexKey])),
        categoryLabels: input.categories,
        nodeColor: (id, i) => paintRef(input.paint, sankeyNodeRef(id), positionalColor(palette, i)),
        animation: chartAnimation("sankey", input.motion),
      });
    }),
    cap: "never",
    metrics: "first",
  },
  // BUILT AND DISCARDED, and that is not a gap. This style has its own
  // renderer: `routeChartType` answers "funnel_explorer" and both surfaces
  // early-return to it (`ChartWithControls`, and `ChartFrame` on a report).
  // But the dashboard builds its option in a `useMemo` that runs for EVERY
  // chart type before those returns, so this entry has to produce something
  // cheap and must not throw - a builder that raised here would take down
  // every funnel tile on the page. `SHARED_LOOP` is that something, and no
  // reader ever sees its output.
  //
  // The entry exists because the key type demands one, which is the whole
  // point of this map: `funnel_explorer` joined `CHART_STYLES` in W4.3 and
  // this was a COMPILE error until it was answered, rather than a style
  // quietly falling through to bars.
  funnel_explorer: { ...SHARED_LOOP, metrics: "first" },
  // BUILT AND DISCARDED, for `funnel_explorer`'s reason above: the receipt is
  // HTML (`routeChartType` answers "receipt" and both surfaces early-return),
  // but the dashboard's option `useMemo` runs for every chart type first, so
  // this entry must be cheap and must never throw (plan 2026-10-16-a-receipt-
  // visualization, D7).
  receipt: { ...SHARED_LOOP, metrics: "first" },
  // `never`: row order IS step order (see `buildFunnelOption`), and an
  // "Other" step would be a stage nobody defined. Too many steps is the
  // writer's refusal (at most 8), never a fold here.
  funnel: { build: optionBuilder(buildFunnelOption), cap: "never", metrics: "first" },
};

/**
 * A bridge: a waterfall whose first and last bars are LEVELS, not steps.
 *
 * The same picture as `buildWaterfallSeries` and deliberately the same code —
 * a bridge IS a waterfall, drawn from rows that `services/composition/bridge.py`
 * computed out of two periods instead of rows a query returned. What this
 * wrapper adds is one refusal.
 *
 * WHY THE REFUSAL EXISTS, and it is the reason this is not simply
 * `{ build: seriesBuilder(buildWaterfallSeries) }`. The backend guarantees
 * anchors only on the paths where a comparison was actually requested. A
 * viewer who turns the dashboard's comparison off, or a spec served from a
 * cache written before the bridge existed, reaches this builder with the
 * MEMBERS OF ONE PERIOD and no anchors at all. Drawn as a waterfall those
 * members would stack into a running total that means nothing, under a title
 * promising the change between two periods — a wrong picture, and the kind
 * nobody reports because nothing looks broken.
 *
 * So: no anchor, no bars. An empty chart is a state the reader can see and
 * ask about; a plausible wrong one is not. The sentence explaining it comes
 * from the server (`bridge.NEEDS_A_COMPARISON`), which is the only side that
 * knows WHICH of the reasons applied.
 */
function buildBridgeSeries(input: SeriesBuilderInput): any[] {
  const anchored = input.rows.some((r) => r?.[WATERFALL_ANCHOR_KEY] === true);
  if (!anchored) return [];
  return buildWaterfallSeries(input);
}

/**
 * Marks the step the server added because the steps between two levels did
 * not land on the second one (`services/composition/step_waterfall.py`,
 * `RESIDUAL_KEY`). Pinned equal to the server's by a backend test.
 */
export const STEP_WATERFALL_RESIDUAL_KEY = "__residual";

/** The tooltip's name for a step waterfall's one series. */
const STEP_WATERFALL_SERIES_NAME = "Amount";

/** What a step with no value for the period shows instead of a bar. */
export const STEP_WATERFALL_HOLE_LABEL = "—";

/** A residual is neither a rise nor a fall of anything the author named. */
const STEP_WATERFALL_RESIDUAL_COLOR = "#9CA3AF";

// `stepWaterfallColors` (the levels, rises and falls picked from the
// palette) lives in `seriesColors.ts`, with the other positional picks.
export { stepWaterfallColors } from "./seriesColors";

/**
 * A step waterfall (`metric_waterfall`): the same two-series picture as
 * `buildWaterfallSeries`, over rows the SERVER built from one single-value row
 * (plan 2026-10-16-a-waterfall-with-subtotals, D3). Every level is anchored and
 * is a value of the query; every step carries its sign already. Nothing is
 * computed here but the drawing offsets.
 *
 * Three things differ from the plain waterfall, each because the server says
 * something the plain waterfall's rows never do:
 *
 * - NO ANCHOR, NO BARS (the bridge's refusal). A spec served from a stale cache
 *   or a path that skipped the chokepoint would carry the query's raw metrics;
 *   drawn as a running sum under this title they would be a wrong picture
 *   nobody reports.
 * - A HOLE is a hole: a step whose value is null draws no bar, only a "—" at the
 *   running total. `toNum` would draw it as a confident zero-height step, which
 *   reads the same as a measured zero.
 * - A RESIDUAL (`__residual`) is drawn neutral and hatched: it is the difference
 *   between a level and its steps, not a step anyone named.
 */
function buildStepWaterfallSeries({ rows, yKeys, labels, colors, paint }: SeriesBuilderInput): any[] {
  if (!rows.some((r) => r?.[WATERFALL_ANCHOR_KEY] === true)) return [];
  const key = yKeys[0];
  if (key == null) return [];
  // On a dashboard the levels are `r:total` and the steps `r:increase` /
  // `r:decrease`, the roles a plain waterfall has; a role with nothing set
  // keeps the palette's pick.
  const picked = stepWaterfallColors(colors);
  const levelColor = paintRef(paint, "r:total", picked.level);
  const upColor = paintRef(paint, "r:increase", picked.up);
  const downColor = paintRef(paint, "r:decrease", picked.down);

  const offsets: (number | null)[] = [];
  const data: any[] = [];
  let running = 0;
  for (const row of rows) {
    const v = measuredNumber(row?.[key]);
    if (v == null) {
      offsets.push(running);
      data.push({
        value: 0,
        itemStyle: { color: "transparent" },
        // The residual's grey: legible on the light and the dark card alike.
        label: { show: true, position: "top", formatter: STEP_WATERFALL_HOLE_LABEL, color: STEP_WATERFALL_RESIDUAL_COLOR },
      });
      continue;
    }
    if (row?.[WATERFALL_ANCHOR_KEY] === true) {
      offsets.push(Math.min(0, v));
      running = v;
      data.push({
        value: Math.abs(v),
        itemStyle: { color: barGradient(levelColor, false), borderRadius: [3, 3, 3, 3] },
      });
      continue;
    }
    const before = running;
    const after = running + v;
    offsets.push(Math.min(before, after));
    running = after;
    const residual = row?.[STEP_WATERFALL_RESIDUAL_KEY] === true;
    data.push({
      value: Math.abs(v),
      itemStyle: residual
        ? {
            color: STEP_WATERFALL_RESIDUAL_COLOR,
            opacity: 0.7,
            borderRadius: [3, 3, 3, 3],
            decal: {
              symbol: "rect",
              dashArrayX: [1, 0],
              dashArrayY: [2, 4],
              rotation: -Math.PI / 4,
              color: "rgba(255, 255, 255, 0.55)",
            },
          }
        : {
            color: barGradient(v < 0 ? downColor : upColor, false),
            borderRadius: [3, 3, 3, 3],
          },
    });
  }

  return [
    {
      name: WATERFALL_BASE_SERIES_NAME,
      type: "bar",
      stack: WATERFALL_STACK,
      // One stack whatever the sign: with the default ("samesign") a
      // positive bar never stacks on a negative base, so a step that crosses
      // zero drew from zero instead of from the running total (gate A6).
      stackStrategy: "all",
      yAxisIndex: 0,
      barMaxWidth: 50,
      silent: true,
      legendHoverLink: false,
      itemStyle: { color: "transparent" },
      emphasis: { disabled: true },
      tooltip: { show: false },
      data: offsets,
      z: 0,
    },
    {
      // The steps are the categories; the one series needs a word for the
      // tooltip line, never the raw y-key ("value").
      name: labels?.[key] ?? STEP_WATERFALL_SERIES_NAME,
      type: "bar",
      stack: WATERFALL_STACK,
      // One stack whatever the sign: with the default ("samesign") a
      // positive bar never stacks on a negative base, so a step that crosses
      // zero drew from zero instead of from the running total (gate A6).
      stackStrategy: "all",
      yAxisIndex: 0,
      barMaxWidth: 50,
      emphasis: { focus: "series" as const },
      data,
      z: 1,
    },
  ];
}

/**
 * The recipe for a spelling, falling back to the common case.
 *
 * Widened to `string` at the LOOKUP rather than in the map's declaration: the
 * accepted set also holds pre-registry chat spellings (`timeseries`,
 * `stacked-bar`, `""`) that are not chart styles and never will be. They are
 * all drawn by the shared loop and all cap the ordinary way, which is exactly
 * what `SHARED_LOOP` says — so the fallback is the right answer for them
 * rather than a hole. A REGISTRY spelling can never reach the fallback: the
 * map's key type makes a missing entry a compile error.
 */
function recipeFor(chartType: string): ChartRecipe {
  // An own-property test, not `map[key] ?? fallback`: a spelling such as
  // "constructor" would otherwise find an inherited member, not `undefined`.
  // (`Object.hasOwn` is ES2022; this package targets ES2020.)
  return Object.prototype.hasOwnProperty.call(CHART_RECIPES, chartType)
    ? CHART_RECIPES[chartType as keyof typeof CHART_RECIPES]
    : FALLBACK_RECIPE;
}

/** Every chart style that has a recipe — for tests that pin a per-style table. */
export const RECIPE_CHART_TYPES: readonly string[] = Object.freeze(Object.keys(CHART_RECIPES));

/** What this chart style does with `spec.y_keys` (see `MetricChannels`). */
export function metricChannelsFor(chartType: string | null | undefined): MetricChannels {
  return recipeFor((chartType || "").toLowerCase()).metrics;
}

/** How this chart style handles more rows than fit on screen. */
export function categoryCapFor(chartType: string | null | undefined): CategoryCap {
  return recipeFor((chartType || "").toLowerCase()).cap;
}

/**
 * Everything the builder needs besides the spec, by name.
 *
 * The positional form below came to seven arguments, and the eighth was going
 * to be the report page's legend. Nobody should have to count to eight to pass
 * one field to a package three surfaces share, so the new form is this object
 * and the positional form stays exactly as it was for the callers that have
 * it. Both reach the same builder; an option built from equal fields is
 * byte-identical whichever form asked for it.
 */
export type ChartBuildOptions = {
  smoothLines?: boolean;
  chartColors?: string[];
  activeEndLabel?: string;
  highlightsEnabled?: boolean;
  rowPreviews?: Record<string, RowPreview>;
  motion?: ChartMotion;
  /**
   * Where the chart is drawn.
   *
   * `"auto"` (the default) is every surface that scrolls and hovers: a chat
   * message, a dashboard tile, the flowing report. The legend pages when it
   * does not fit, which is right when the reader can click the pager.
   *
   * `"page"` is a REPORT PAGE — printed, or a cell that clips. The legend is
   * plain, horizontal and under the plot, it wraps, and the space it takes is
   * reserved before the plot is laid out. See `pageLegendGeometry`.
   */
  legend?: "auto" | "page";
  /** The canvas the surface measured, in CSS px. Only read when
   *  `legend: "page"`; the fallbacks are the narrow A4 case. */
  canvasWidthPx?: number;
  canvasHeightPx?: number;
  /** Click-to-filter (plan 2026-10-05 dashboard interactivity, B4): the
   *  dashboard passes it, no other surface does. Absent, the option is
   *  exactly what it was; present, the series that can be clicked carry
   *  their datums (`./datumClick.ts`) and the tile's selection is drawn. */
  interaction?: DatumInteraction;
  /**
   * The reader's legend selection (`legendselectchanged`'s `selected`, keyed
   * by series name), carried into the option. EChart sets every option with
   * `notMerge`, so a rebuild without it shows every series again. On a
   * stacked metrics chart a deselected part also drops the total labels: they
   * print the total of ALL parts and would float above a shorter column (the
   * tooltip's Total already follows the legend). Only the dashboard and chat
   * surface pass it; the report sandbox has no legend state, so a static
   * edition keeps its labels.
   */
  legendSelected?: Readonly<Record<string, boolean>>;
  /**
   * A dashboard tile's chart colours (ADR-0476): the block's `chart_colors`,
   * the dashboard's pins and the palettes they resolve against. Only the
   * dashboard tile passes it — always, also with nothing set, so every
   * dashboard chart colours its dimension values by value. Absent (chat, a
   * report, an MCP App) the option is byte-identical to what it was before;
   * `__tests__/colorLayers.test.ts` pins that.
   *
   * With it, every series, slice, tile, step, node and role takes its ref's
   * resolved colour (`resolveSeriesColors`); what the map leaves out (a
   * measure or role with nothing set) keeps its positional pick, taken from
   * the chart's palette in force instead of `chartColors`.
   */
  colorLayers?: ChartColorLayers;
};

/** Exported for unit tests — the option builder is a pure function of its inputs. */
export function buildEchartsOption(spec: BackendChartSpec, options?: ChartBuildOptions): EChartsOption;
export function buildEchartsOption(
  spec: BackendChartSpec,
  smoothLines?: boolean,
  chartColors?: string[],
  activeEndLabel?: string,
  highlightsEnabled?: boolean,
  rowPreviews?: Record<string, RowPreview>,
  motion?: ChartMotion,
): EChartsOption;
export function buildEchartsOption(
  spec: BackendChartSpec,
  smoothLinesOrOptions: boolean | ChartBuildOptions = false,
  chartColors: string[] = DEFAULT_CHART_COLORS,
  activeEndLabel?: string,
  highlightsEnabled: boolean = true,
  rowPreviews?: Record<string, RowPreview>,
  motion: ChartMotion = "full",
): EChartsOption {
  const opts: ChartBuildOptions =
    typeof smoothLinesOrOptions === "object" && smoothLinesOrOptions !== null
      ? smoothLinesOrOptions
      : { smoothLines: smoothLinesOrOptions, chartColors, activeEndLabel, highlightsEnabled, rowPreviews, motion };
  return buildWithOptions(spec, opts, null);
}

/**
 * The colour every series ref of a dashboard chart is painted in, as the
 * builder paints it: an override, a pin, the by-value colour, or (for a
 * measure or a role with nothing set) the positional pick. In
 * `seriesRefsOf` order. For the Format pane's swatches and its "Pin current
 * colours"; read from the same build the tile draws, so it cannot drift from
 * the picture.
 */
export function drawnSeriesColors(
  spec: BackendChartSpec,
  options: ChartBuildOptions & { colorLayers: ChartColorLayers },
): Map<SeriesRef, string> {
  const painted = new Map<SeriesRef, string>();
  buildWithOptions(spec, options, painted);
  const drawn = new Map<SeriesRef, string>();
  for (const ref of seriesRefsOf(spec)) {
    const color = painted.get(ref);
    if (color !== undefined) drawn.set(ref, color);
  }
  return drawn;
}

function buildWithOptions(
  spec: BackendChartSpec,
  opts: ChartBuildOptions,
  colorSink: Map<SeriesRef, string> | null,
): EChartsOption {
  // A page canvas exists only when the surface asked for the page legend, so
  // every other surface reaches `buildEchartsOptionForSpec` with `null` and
  // takes the branches it has always taken.
  const page: PageCanvas | null =
    opts.legend === "page"
      ? {
          widthPx: opts.canvasWidthPx ?? PAGE_CANVAS_WIDTH_FALLBACK_PX,
          heightPx: opts.canvasHeightPx ?? PAGE_CANVAS_HEIGHT_FALLBACK_PX,
        }
      : null;
  return clampDanglingAxisIndices(
    buildEchartsOptionForSpec(
      spec,
      opts.smoothLines ?? false,
      opts.chartColors ?? DEFAULT_CHART_COLORS,
      opts.activeEndLabel,
      opts.highlightsEnabled ?? true,
      opts.rowPreviews,
      opts.motion ?? "full",
      page,
      opts.interaction ?? null,
      opts.legendSelected ?? null,
      opts.colorLayers ?? null,
      colorSink,
    ),
  );
}

function buildEchartsOptionForSpec(
  spec: BackendChartSpec,
  smoothLines: boolean = false,
  chartColors: string[] = DEFAULT_CHART_COLORS,
  activeEndLabel?: string,
  highlightsEnabled: boolean = true,
  rowPreviews?: Record<string, RowPreview>,
  motion: ChartMotion = "full",
  page: PageCanvas | null = null,
  interaction: DatumInteraction | null = null,
  legendSelected: Readonly<Record<string, boolean>> | null = null,
  colorLayers: ChartColorLayers | null = null,
  colorSink: Map<SeriesRef, string> | null = null,
): EChartsOption {
  // A dashboard tile's colours (ADR-0476), resolved ONCE over the series this
  // spec draws, before anything below reorders or trims its rows: the Format
  // pane lists `seriesRefsOf` of this same spec, so a row in the pane and the
  // item it colours are keyed alike. `null` everywhere else, and every colour
  // site below then takes the branch it always took.
  const layered = colorLayers ? resolveChartColors(spec, colorLayers) : null;
  // Every colour site below paints through this: the ref's resolved colour,
  // else the site's own pick; what it painted is recorded once per ref for
  // `drawnSeriesColors`.
  const record = (ref: SeriesRef, color: string | undefined) => {
    if (colorSink && color !== undefined && !colorSink.has(ref)) colorSink.set(ref, color);
  };
  const paint: RefPainter | null = layered
    ? (ref, fallback) => {
        const color = layered.colors.get(ref) ?? fallback;
        record(ref, color);
        return color;
      }
    : null;
  // What the map leaves out keeps its positional pick, from the palette the
  // chart chose (`chart_colors.palette`) rather than the account's.
  if (layered) chartColors = layered.palette;
  // A KPI picture (a bullet, a gauge) is one value, not a series over an
  // index: it leaves before the date sorting and the cartesian assembly below,
  // to its own leg (`./kpiPictures.ts`, plan 2026-10-05 finance chart types).
  if (isKpiPictureChartType(spec.chart_type)) {
    const measureRef: SeriesRef | null = spec.y_keys?.[0] ? `m:${spec.y_keys[0]}` : null;
    return buildKpiPictureOption(spec.chart_type as string, {
      spec,
      neutralColor: positionalColor(chartColors, 0) ?? "#5470c6",
      ...(layered && colorLayers
        ? {
            seriesColors: {
              // The chart's own override only, without the dashboard's pins:
              // the on/off-track colour is a signal, and a pin set from a
              // line chart must not silently remove it from every picture.
              measureOverride: measureRef
                ? resolveSeriesColors([measureRef], { chartColors: colorLayers.chartColors }, colorLayers.palettes).get(
                    measureRef,
                  )
                : undefined,
              measure: measureRef ? layered.colors.get(measureRef) : undefined,
              target: layered.colors.get("r:target"),
            },
            painted: (part: "measure" | "target", color: string) =>
              record(part === "target" ? "r:target" : measureRef ?? "", color),
          }
        : {}),
      formatValue: (v) => formatTooltipValue(v, spec.metric_format),
    });
  }
  const xKey = spec.chart_index_by;
  // The time-axis contract (ADR-0266): with a valid `time_axis` the rows ARE
  // the requested period — one bucket each, a bucket without data is `null`.
  // The axis is then never trimmed, a hole is drawn as a gap (never as 0),
  // and the buckets that are behind get the amber zone. A malformed stamp
  // reads as absent (`readTimeAxis`), so it can never steer the picture.
  const axis = readTimeAxis(spec as unknown as Record<string, unknown>);
  // Chronological rows, trailing empty periods stripped (`drawnRows.ts`):
  // the same rows `seriesRefsOf` lists the series of.
  const sorted = sortAndTrimDateRows(spec, xKey);
  // A breakdown on a date part names its series by value ("Oct", not "10";
  // plan 2026-10-16 date parts, S1) through `labels`, which every legend,
  // tooltip and series name below reads.
  spec = withDatePartSeriesLabels(sorted.spec);
  const formatDates = sorted.formatDates;
  // A date-part index ("Order date · month of year") reads in the block's
  // `date_part_label` style; the row keeps the integer, so a click carries it.
  const partLabel = formatDates ? null : datePartLabellerFor(spec, xKey);
  const xRaw = (spec.rows ?? []).map((r) => toStr(r[xKey]));
  const dateGrain = axis?.grain ?? spec.time_grain ?? (formatDates ? detectDateGrain(xRaw) : "monthly");
  // The bucket the incomplete-period vocabulary marks: on a time axis the one
  // `period_completeness.index_value` names (a month axis on the 16th marks
  // September, not December); otherwise the last row, as it always was.
  const incompleteIndex = (() => {
    const pcEarly = spec.period_completeness;
    const count = spec.rows?.length ?? 0;
    if (!pcEarly || count === 0) return -1;
    if (!axis) return count - 1;
    const wanted = String(pcEarly.index_value ?? "").slice(0, 10);
    return (spec.rows ?? []).findIndex((r) => toStr(r[xKey]).slice(0, 10) === wanted);
  })();
  const x = formatDates ? xRaw.map(v => formatDateLabel(v, dateGrain)) : partLabel ? xRaw.map(partLabel) : xRaw;
  // Click datums, attached at the returns below that draw one data item per
  // row of `spec.rows` (pie, bars, lines). Every other return (KPI pictures,
  // the recipe builders: sankey, treemap, scatter, ...) is not clickable on
  // purpose: its marks are not one value of one dimension.
  const withDatums = (built: EChartsOption, yKeyOf: (seriesIndex: number) => string | undefined): EChartsOption =>
    interaction
      ? attachChartDatums(built, { spec, interaction, timeIndex: axis != null, grain: dateGrain, yKeyOf })
      : built;

  const chartType = (spec.chart_type || "").toLowerCase();
  // `donut` is a pie with a hole and `area` is a line with a real fill, so
  // both ride the branch of the type they are a variant of. Without this they
  // fall through to `defaultSeriesType = "bar"` below and draw as vertical
  // bars — they are in ECHARTS_SERIES_CHART_TYPES, so not even the
  // unsupported-type empty state catches them. `line_smoothed` and
  // `area_stacked` are the same trick one level deeper: line variants that
  // differ from their base only in the curve and the stack, so an exact
  // `=== "line"` / `=== "area"` here would have drawn them as bars.
  const isDonut = chartType === "donut";
  const isPie = chartType === "pie" || isDonut;
  const isArea = isAreaFamily(chartType);
  const isLine = isLineFamily(chartType);
  const smoothedByType = isSmoothedByChartType(chartType);
  const isStacked = isStackedChartType(chartType);
  const isCombo = chartType === "combo";
  const isHorizontal = chartType.includes("horizontal");
  // The step waterfall's visible bars carry magnitudes too; its tooltip reads
  // the signed value from the row, and a null there is a hole, never a 0.
  const isWaterfall = chartType === "waterfall" || chartType === "metric_waterfall";
  // A 100% stack IS a stack — `isStacked` already fires on the "stack" in its
  // spelling — so it takes the shared stacked path with its rows rewritten
  // first. Done here, before `allYValues` below reads them, so the fractions
  // are what the percentage detection and the axis formatting see.
  const isHundredPercentStack = HUNDRED_PERCENT_STACK_CHART_TYPES.has(chartType);
  if (isHundredPercentStack) {
    spec = { ...spec, rows: normalizeRowsTo100Percent(spec.rows ?? [], spec.y_keys ?? []) };
  }
  // A stacked metrics chart: every key is a part of one total.
  const isPartsStack = metricChannelsFor(chartType) === "parts";

  // Highlights: only for supported chart types
  const activeHighlights = highlightsEnabled && supportsHighlights(chartType)
    ? spec.highlights ?? []
    : [];

  // Detect percentage data
  const yKeys = spec.y_keys ?? [];
  const allYValues = (spec.rows ?? []).flatMap(r => yKeys.map(k => toNum(r[k])));
  const isFraction = isPercentageData(allYValues);  // 0-1 range → needs ×100
  const isAlreadyPct = isAlreadyPercentageData(allYValues, yKeys);  // >1 range + key suggests % → just add suffix
  const isPercentage = isFraction;  // backward compat: only true for fractions

  /**
   * A chart style whose builder owns the WHOLE option leaves here, before the
   * shared cartesian assembly below builds a grid, a category x-axis and an
   * axis-triggered tooltip that its picture has no use for.
   *
   * Dispatched from `CHART_RECIPES` rather than from a condition of its own:
   * the reason `scatter` drew as bars for months is that membership in the
   * accepted set and the existence of a branch were two separate facts, and
   * only one of them was checked. Here they are the same fact.
   */
  const recipe = recipeFor(chartType);
  if (recipe.build?.kind === "option") {
    return recipe.build.build({
      rows: spec.rows ?? [],
      yKeys,
      labels: spec.labels,
      colors: chartColors,
      paint,
      categories: x,
      secondCategories: (() => {
        const second = spec.dimension_keys?.[1];
        const secondLabel = datePartLabellerFor(spec, second);
        return second ? (spec.rows ?? []).map((r) => (secondLabel ? secondLabel(r[second]) : toStr(r[second]))) : [];
      })(),
      formatValue: (v: number) => formatTooltipValue(v, spec.metric_format),
      isPercentage,
      motion,
      indexKey: xKey,
      comparisonRows: Array.isArray(spec.comparison_rows) ? spec.comparison_rows : [],
      comparisonLabel: spec.comparison_label ?? undefined,
      breakdownKey: spec.breakdown_key || undefined,
    });
  }

  // PIE
  if (isPie) {
    const key = spec.y_keys?.[0];
    const data =
      key && spec.rows
        ? spec.rows.map((r) => {
            const rawName = toStr(r[xKey]);
            const slice = {
              name: formatDates ? formatDateLabel(rawName, dateGrain) : partLabel ? partLabel(rawName) : rawName,
              value: toNum(r[key]),
            };
            // On a dashboard a slice carries its value's resolved colour;
            // elsewhere `option.color` paints the slices by position.
            const sliceColor = paintRef(paint, dimensionValueRef(r[xKey]), undefined);
            return sliceColor ? { ...slice, itemStyle: { color: sliceColor } } : slice;
          })
        : [];

    // For pie charts, legend item count is based on data points (slices), not y_keys
    const pieSliceCount = data.length;
    const pieLegendAtBottom = pieSliceCount <= 3;

    // Use distributed colors for better contrast with few slices
    const pieColors = selectDistributedColors(chartColors, pieSliceCount);

    // On a page a pie has no grid to give space back, so the reservation is
    // geometric: the legend keeps its rows at the bottom and the pie is drawn
    // inside what is left. A slice count is not capped the way a series count
    // is (`BREAKDOWN_SERIES_CAP` bounds series, not slices), so this is the
    // branch that has to survive ten-plus entries.
    const pieNames = data.map((d) => d.name);
    const piePage = page ? pageLegendGeometry(pieNames, page.widthPx) : null;
    const piePlotHeightPx = piePage ? Math.max(40, page!.heightPx - pageLegendReservePx(piePage, page!.heightPx)) : 0;
    // Same 70%-of-the-shorter-side the screen pie draws, against the plot area
    // instead of the whole canvas, in px because the caller measured px.
    const pieOuterRadiusPx = piePage
      ? Math.max(16, Math.floor(0.35 * Math.min(page!.widthPx, piePlotHeightPx)))
      : 0;

    return withDatums({
      ...chartAnimation("pie", motion),
      color: pieColors.length > 0 ? pieColors : chartColors,
      tooltip: {
        trigger: "item",
        valueFormatter: isPercentage
          ? (value: any) => formatPercentage(Number(value))
          : (value: any) => formatTooltipValue(Number(value)),
      },
      legend: piePage
        ? { ...piePage.legend, data: pieNames }
        : pieLegendAtBottom
        ? {
            type: "scroll",
            orient: "horizontal",
            bottom: 4,
            left: "center",
            textStyle: { fontSize: 11 },
          }
        : {
            type: "scroll",
            orient: "vertical",
            right: 0,
            top: "middle",
            textStyle: { width: 100, overflow: "break", fontSize: 11 },
            formatter: formatLegendName,
          },
      series: [
        {
          type: "pie",
          // Same ring as react/PieChart draws for its `donut` prop,
          // so a chart looks the same in a report and on a tile.
          radius: piePage
            ? (isDonut ? [Math.round((pieOuterRadiusPx * 4) / 7), pieOuterRadiusPx] : pieOuterRadiusPx)
            : (isDonut ? ["40%", "70%"] : "70%"),
          ...(piePage ? { center: ["50%", Math.round(piePlotHeightPx / 2)] } : {}),
          data,
          label: isPercentage ? {
            formatter: (params: any) => `${params.name}: ${formatPercentage(params.value)}`,
          } : undefined,
        },
      ],
    }, (i) => (i === 0 ? key : undefined));
  }

  // Check if we need dual Y-axis
  const seriesConfigMap = new Map(
    (spec.series_config ?? []).map((c) => [c.key, c])
  );
  /**
   * A secondary y-axis exists ONLY on the vertical branch (`yAxis: yAxisConfig`
   * further down). On a horizontal chart the value axis is the X axis and the
   * y-axis is the category axis, so `yAxisIndex: 1` points at an axis this
   * option never defines — ECharts 6 throws `yAxis "0" not found`
   * (model/referHelper.js) and `EChart` renders an empty box. The
   * horizontal-grouped comparison branch does define a `yAxis[1]`, but that is
   * the HIDDEN DUPLICATE CATEGORY axis for the delta overlay: a real series
   * landing there draws against the wrong axis instead of throwing.
   *
   * Sharing one value axis is also the honest rendering here — the horizontal
   * value axis is hidden (`xAxis: { show: false }`), so two bars in the same row
   * on two different invisible scales would read as directly comparable.
   */
  const supportsSecondaryYAxis = !isHorizontal;
  const hasExplicitDualAxis =
    supportsSecondaryYAxis && (spec.series_config ?? []).some((c) => c.yAxisIndex === 1);

  // Auto-detect dual axis: when series have very different scales (>5x ratio),
  // put the smaller-scale series on the secondary axis for readability.
  //
  // NEVER inside a stack, and that guard was missing until 2026-09-07. A stack
  // adds its parts and draws them as one column; putting the smallest part on
  // a second scale makes that column two scales tall, so the segment heights
  // no longer sum to the total the bar claims to show. On a 100% stack it is
  // worse still — the components would no longer add up to the full height
  // that is the entire premise of the chart. Stacked parts always share a unit:
  // a breakdown stack splits ONE metric by a dimension, and a stacked metrics
  // chart (`metrics: "parts"`, plan 2026-10-17) stacks parts of one total, whose
  // write rule refuses two different catalog formats among them — so a 5x
  // spread between them is a small member, not a second scale.
  const autoSecondaryKeys = new Set<string>();
  const allYKeys = spec.y_keys ?? [];
  if (supportsSecondaryYAxis && !hasExplicitDualAxis && !isStacked && allYKeys.length >= 2 && !isPie) {
    const seriesMaxes = allYKeys.map((k) => {
      const vals = (spec.rows ?? []).map((r) => Math.abs(toNum(r[k])));
      return { key: k, max: Math.max(...vals, 0) };
    });
    const globalMax = Math.max(...seriesMaxes.map((s) => s.max), 1);
    for (const s of seriesMaxes) {
      if (s.max > 0 && globalMax / s.max > 5) {
        autoSecondaryKeys.add(s.key);
      }
    }
  }
  const hasDualAxis = hasExplicitDualAxis || autoSecondaryKeys.size > 0;

  /**
   * The y-axis a series renders against. Single source of truth for all four
   * stamping sites (main series, comparison line, comparison bar, std-dev band)
   * so the "index 1 only exists on the vertical branch" rule cannot drift.
   */
  const resolveYAxisIndex = (config: SeriesConfig | undefined, key?: string): 0 | 1 => {
    if (!supportsSecondaryYAxis) return 0;
    if (config?.yAxisIndex != null) return config.yAxisIndex;
    return key != null && autoSecondaryKeys.has(key) ? 1 : 0;
  };

  // LINE / BAR / COMBO
  const defaultSeriesType: "line" | "bar" = isLine ? "line" : "bar";

  // Pre-select distributed colors for better contrast with few series
  const seriesCount = (spec.y_keys ?? []).length;
  const distributedColorAt = distributedPick(chartColors, seriesCount);
  /** y_key -> the colour its main series drew, so an overlay of that series
   *  (the comparison ghost, the plan line, the incomplete-period dash, the
   *  horizontal bar's pair) can follow it. Read only with colour layers:
   *  without them each overlay keeps the pick it always made, mismatches
   *  included (ADR-0476: the fix is dashboard-only). */
  const drawnColorByKey = new Map<string, string>();
  const overlayColorOf = (k: string): string | undefined => (paint ? drawnColorByKey.get(k) : undefined);

  // For combo charts: track which series are lines/bars for color assignment
  let comboLineColorIdx = 0;
  let comboBarColorIdx = 0;

  /**
   * The shared producer: one series per metric key. Wrapped in a function so
   * a chart type with its own builder does not run it and throw the result
   * away — and so the hundred lines below stay exactly where they are.
   */
  const buildSharedLoopSeries = (): any[] => (spec.y_keys ?? []).map((k, idx) => {
    const config = seriesConfigMap.get(k);

    // Determine series type: use config, or for combo default first to bar, rest to line
    let seriesType: "line" | "bar" = defaultSeriesType;
    if (config?.type) {
      seriesType = config.type;
    } else if (isCombo) {
      seriesType = idx === 0 ? "bar" : "line";
    }

    const yAxisIndex = resolveYAxisIndex(config, k);

    // Determine color:
    // 1. Respect explicit config colors
    // 2. For combo charts: lines get darkest, bars get next-darkest
    // 3. For other charts: use distributed colors for better contrast
    let color: string;
    if (config?.color) {
      color = config.color;
    } else if (isCombo && seriesType === "line") {
      // Lines in combo charts get the darkest colors (index 0, 1, ...)
      color = asDrawnColor(positionalColor(chartColors, comboLineColorIdx));
      comboLineColorIdx++;
    } else if (isCombo && seriesType === "bar") {
      // Bars in combo charts get dark colors too, but offset from lines (index 2, 3, ...)
      const barStartIndex = 2;
      color = asDrawnColor(positionalColor(chartColors, barStartIndex + comboBarColorIdx));
      comboBarColorIdx++;
    } else {
      color = asDrawnColor(distributedColorAt(idx));
    }
    // 4. On a dashboard, the series ref's resolved colour beats all three
    //    (ADR-0476). Applied after them, so a combo's counters still advance
    //    and the series after an overridden one keep their own picks.
    color = paintRef(paint, cartesianSeriesRef(k, spec.breakdown_key), color);
    drawnColorByKey.set(k, color);


    return {
      name: spec.labels?.[k] ?? k,
      type: seriesType,
      // A stack is a stack whichever mark draws it. Pinning this to `bar` left
      // `area_stacked` — bands that add up, which is the whole chart — as a
      // pile of independent areas drawn over each other.
      stack: isStacked ? "total" : undefined,
      barMaxWidth: seriesType === "bar" ? 50 : undefined,
      showSymbol: seriesType === "line" ? true : undefined,
      symbolSize: seriesType === "line" ? 4 : undefined,
      // Two producers, deliberately OR'd: the chart type (`line_smoothed` IS
      // the curve) and the reader's Smooth switch (a plain line, on demand).
      // The switch is hidden for a type that already decided — see
      // `showSmoothToggle` — so this can never be a control that does nothing.
      smooth: seriesType === "line" ? smoothedByType || smoothLines : undefined,
      yAxisIndex,
      itemStyle: seriesType === "bar"
        ? { color: barGradient(color, isHorizontal), borderRadius: [3, 3, 3, 3] }
        : { color },
      lineStyle: seriesType === "line" ? { color, width: 1.5 } : undefined,
      ...(seriesType === "line" ? {
        areaStyle: {
          color: {
            type: "linear" as const,
            x: 0, y: 0, x2: 0, y2: 1,
            // An area chart is a line chart whose fill is the point, so it
            // gets a fill you can actually see; a line chart keeps the faint
            // wash that only grounds the line.
            colorStops: isArea
              ? [
                  { offset: 0, color: color + "66" },
                  { offset: 1, color: color + "10" },
                ]
              : [
                  { offset: 0, color: color + "18" },
                  { offset: 0.7, color: color + "08" },
                  { offset: 1, color: color + "00" },
                ],
          },
        },
      } : {}),
      emphasis: { focus: "series" as const },
      data: (spec.rows ?? []).map((r, dataIdx) => {
        // On a time axis a hole is a gap, never a zero: `measuredNumber` keeps
        // `null`, and ECharts leaves the segment out (`connectNulls` is off).
        const val = axis ? measuredNumber(r[k]) : toNum(r[k]);
        const rawIdx = toStr(r[spec.chart_index_by]);
        const isLastPoint = dataIdx === incompleteIndex;
        if (highlightsEnabled && activeHighlights?.length) {
          const hl = findHighlight(activeHighlights, rawIdx, k);
          if (hl) {
            if (seriesType === "bar" && (!isCombo || hl.y_key === k)) {
              return { value: val, itemStyle: { color: barGradient(sentimentColor(hl.sentiment), isHorizontal), borderRadius: [3, 3, 3, 3] } };
            }
            if (seriesType === "line") {
              const hlColor = sentimentColor(hl.sentiment);
              return {
                value: val,
                symbolSize: 8,
                itemStyle: {
                  color: hlColor,
                  shadowColor: hlColor.replace("0.85)", "0.5)"),
                  shadowBlur: 6,
                },
              };
            }
          }
        }
        // Incomplete period: orange dot on last data point
        if (isLastPoint && spec.period_completeness) {
          if (seriesType === "line") {
            return {
              value: val,
              symbolSize: 8,
              itemStyle: {
                color: INCOMPLETE_PERIOD_COLOR,
                borderColor: "#fff",
                borderWidth: 2,
                shadowColor: "rgba(245, 158, 11, 0.4)",
                shadowBlur: 6,
              },
            };
          }
          if (seriesType === "bar") {
            return {
              value: val,
              itemStyle: {
                opacity: 0.6,
                borderColor: INCOMPLETE_PERIOD_COLOR,
                borderWidth: 2,
                borderType: "dashed" as const,
                borderRadius: [3, 3, 3, 3],
              },
            };
          }
        }
        return val;
      }),
      ...(spec.comparison_rows?.length && seriesType === "bar" ? { z: 2 } : {}),
    } as any;
  });

  const preparedSeriesBuilder = recipe.build?.kind === "series" ? recipe.build.build : null;
  const series: any[] = preparedSeriesBuilder
    ? preparedSeriesBuilder({
        rows: spec.rows ?? [],
        yKeys: spec.y_keys ?? [],
        labels: spec.labels,
        colors: chartColors,
        paint,
      })
    : buildSharedLoopSeries();

  // Build comparison series (period-over-period overlay)
  const hasComparison = !!(spec.comparison_rows?.length);
  // comparison_rows is a union since the table branch keys it as a Record
  // (utils/comparisonAlignment.ts); only the array shape is a series overlay.
  const comparisonSeriesRows = Array.isArray(spec.comparison_rows)
    ? spec.comparison_rows
    : [];
  const comparisonLabel = spec.comparison_label ?? "prev. period";
  // The comparison leg is the account's plan, not a second period
  // (comparison_mode="target"). Two things follow:
  //  - the overlay is always a DASHED LINE, whatever the actuals are drawn
  //    as. A target is a level to read off, not a second bar to compare
  //    column heights with.
  //  - a GUARDRAIL gets no overlay series at all: it is one horizontal bound
  //    (drawn as a markLine below), and a flat series under a coincident
  //    markLine is the same line drawn twice.
  const targetBlock = spec.comparison_target ?? null;
  const isGuardrail = targetBlock?.kind === "guardrail";
  // TWO legs on one block (ADR-0267): the shifted period keeps
  // `comparison_rows`, the account's plan arrives in `target_rows` — same
  // array shape, same alignment to `rows`, one extra dashed line per metric.
  // With the target as the ONLY leg this key is absent and the target IS
  // `comparison_rows` (`comparison_label: "target"`), which is what every
  // line below already drew; the absent key is therefore also what keeps a
  // one-leg picture byte-identical.
  const targetSeriesRows = Array.isArray(spec.target_rows) ? spec.target_rows : [];
  const hasOwnTargetSlot = targetSeriesRows.length > 0;
  // `drawTargetSeries` means "the overlay in `comparison_rows` is the plan",
  // which is only true when the target took that slot.
  const drawTargetSeries = !!targetBlock && !isGuardrail && !hasOwnTargetSlot;
  /**
   * The period-over-period overlay is built by walking `y_keys` and pairing
   * each metric with a second series of its own. A prepared builder owns its
   * whole series list and does not map one key to one series — a waterfall
   * makes two out of one — so there is nothing to pair against. Drawing the
   * overlay anyway would put a second stack of magnitudes beside the first and
   * call it "prev. period", which is not a defined picture.
   */
  // A guardrail draws a markLine and no series — but only over the leg it
  // IS. With the guardrail in `target_rows`, `comparison_rows` still holds a
  // real shifted period and keeps its overlay.
  const drawsComparisonOverlay =
    hasComparison && !(isGuardrail && !hasOwnTargetSlot) && !preparedSeriesBuilder;
  // y_key -> index in `series` of its comparison series, so the pairing pass at the
  // bottom can put each comparison next to its own actual without matching on names.
  const comparisonSeriesIndexByKey = new Map<string, number>();
  /** y_key -> index in `series` of its TARGET line, for the same reason. It is
   *  deliberately NOT in `seriesTypeByKey`: that map decides whether a metric
   *  and its overlay form a BAR pair needing a gap column, and a target is
   *  always a line — a gap beside it would stand next to no column. */
  const targetSeriesIndexByKey = new Map<string, number>();
  const seriesTypeByKey = new Map<string, "line" | "bar">();
  if (drawsComparisonOverlay) {
    for (let i = 0; i < yKeys.length; i++) {
      const k = yKeys[i];
      const mainColor = asDrawnColor(overlayColorOf(k) ?? positionalColor(chartColors, i));
      const lightColor = lightenHex(mainColor, 0.45);
      const cfg = seriesConfigMap.get(k);
      const mainType = cfg?.type ?? (isCombo ? (i === 0 ? "bar" : "line") : defaultSeriesType);
      const sType = drawTargetSeries ? "line" : mainType;
      const compName = `${spec.labels?.[k] ?? k} (${comparisonLabel})`;
      // The pairing pass below reads this to decide whether a metric and its
      // comparison form a BAR pair needing a gap column. A target overlay is a
      // line, so recording the main type here would insert a gap next to a
      // column that has no neighbour.
      seriesTypeByKey.set(k, sType);
      comparisonSeriesIndexByKey.set(k, series.length);

      if (sType === "line") {
        series.push({
          name: compName,
          type: "line",
          showSymbol: false,
          // Same curve as the actual it shadows: a straight comparison under a
          // smoothed line reads as a second, different metric.
          smooth: smoothedByType || smoothLines,
          yAxisIndex: resolveYAxisIndex(cfg, k),
          itemStyle: { color: lightColor },
          lineStyle: { color: lightColor, width: 1.5, type: "dashed" as const },
          emphasis: { focus: "series" as const },
          data: comparisonSeriesRows.map((r) => {
            const v = r[k];
            return v == null ? null : toNum(v);
          }),
        } as any);
      } else {
        series.push({
          name: compName,
          type: "bar",
          barMaxWidth: 50,
          barGap: COMPARISON_BAR_GAP,
          z: 1,
          stack: undefined,
          yAxisIndex: resolveYAxisIndex(cfg, k),
          itemStyle: { color: lightColor, opacity: 0.5 },
          emphasis: { focus: "series" as const },
          data: comparisonSeriesRows.map((r) => {
            const v = r[k];
            return v == null ? null : toNum(v);
          }),
        } as any);
      }
    }
  }

  /**
   * The PLAN line, beside the shifted overlay (ADR-0267). One dashed line per
   * metric out of `target_rows`, drawn with exactly the look a target-only
   * overlay has had since the dashboard's target mode: a target is a level to
   * read off, never a second bar to compare column heights with.
   *
   * Where it deliberately draws NOTHING:
   *  - a GUARDRAIL: one horizontal bound (the markLine below), whichever slot
   *    it arrived in; a flat series under a coincident markLine is the same
   *    line twice;
   *  - a PREPARED series builder (waterfall): it owns its whole series list
   *    and does not map one y_key to one series, so there is nothing to hang
   *    a second leg on — the same reason the shifted overlay stays out;
   *  - the HORIZONTAL bar path: it builds its own series list further down
   *    and renders the comparison as a per-bar delta LABEL. A delta label
   *    against two baselines is not a defined picture, so a horizontal bar
   *    carries the shifted leg only. (Building the series here anyway would
   *    also leak into that path's `series.slice(2)` and draw the plan as a
   *    third bar.)
   */
  const drawsTargetOverlay = hasOwnTargetSlot && !isGuardrail && !preparedSeriesBuilder && !isHorizontal;
  if (drawsTargetOverlay) {
    for (let i = 0; i < yKeys.length; i++) {
      const k = yKeys[i];
      const cfg = seriesConfigMap.get(k);
      const lightColor = lightenHex(asDrawnColor(overlayColorOf(k) ?? positionalColor(chartColors, i)), 0.45);
      targetSeriesIndexByKey.set(k, series.length);
      series.push({
        name: `${spec.labels?.[k] ?? k} (target)`,
        type: "line",
        showSymbol: false,
        smooth: smoothedByType || smoothLines,
        yAxisIndex: resolveYAxisIndex(cfg, k),
        itemStyle: { color: lightColor },
        lineStyle: { color: lightColor, width: 1.5, type: "dashed" as const },
        emphasis: { focus: "series" as const },
        data: targetSeriesRows.map((r) => {
          const v = r[k];
          return v == null ? null : toNum(v);
        }),
      } as any);
    }
  }

  // Build highlight gradient columns for pure line charts (not combo)
  // Each highlight gets a full-height vertical gradient stripe behind the data point
  if (!isCombo && highlightsEnabled && activeHighlights?.length) {
    const rows = spec.rows ?? [];
    // Collect all point highlights for line series
    const pointHls = activeHighlights.filter((h) => h.type === "point");
    if (pointHls.length && (isLine || defaultSeriesType === "line")) {
      const markAreaData = pointHls
        .map((h) => {
          const rowIdx = rows.findIndex((r) => toStr(r[spec.chart_index_by]) === h.index_value);
          if (rowIdx < 0) return null;
          return [
            { xAxis: Math.max(0, rowIdx - 0.5), itemStyle: { color: sentimentBgColor(h.sentiment) } },
            { xAxis: Math.min(rows.length - 1, rowIdx + 0.5) },
          ];
        })
        .filter(Boolean);

      if (markAreaData.length && series.length > 0) {
        // Attach markArea to first line series
        const firstLine = series.find((s: any) => s.type === "line");
        if (firstLine) {
          (firstLine as any).markArea = { silent: true, data: markAreaData };
        }
      }
    }
  }

  // Standard deviation band for line charts (when highlights are enabled)
  // Shows the normal range as a subtle shaded area — outliers clearly fall outside
  if (highlightsEnabled && activeHighlights?.length) {
    const rows = spec.rows ?? [];
    for (const yKey of yKeys) {
      const cfg = seriesConfigMap.get(yKey);
      const sType = cfg?.type ?? defaultSeriesType;
      if (sType !== "line" || rows.length < 4) continue;

      const yAxisIdx = resolveYAxisIndex(cfg, yKey);

      // Calculate mean and std over the MEASURED values: a hole is not a
      // zero, and a null tail (a time axis behind on data) must not drag the
      // mean down and the band open (review R4-code-quality M3).
      const vals = rows
        .map((r) => measuredNumber(r[yKey]))
        .filter((v): v is number => v !== null);
      if (vals.length < 4) continue;
      const mean = vals.reduce((a, b) => a + b, 0) / vals.length;
      const std = Math.sqrt(vals.reduce((a, v) => a + (v - mean) ** 2, 0) / vals.length);

      if (std === 0 || !Number.isFinite(std)) continue;

      // Upper bound (mean + 1.8σ)
      const upper = rows.map(() => mean + 1.8 * std);
      // Lower bound (mean - 1.8σ), clamped to 0
      const lower = rows.map(() => Math.max(0, mean - 1.8 * std));

      // Add upper and lower as dashed boundary lines
      series.push({
        name: `_band_upper_${yKey}`,
        type: "line",
        showSymbol: false,
        silent: true,
        yAxisIndex: yAxisIdx,
        lineStyle: { width: 1, color: "#94a3b8" },
        itemStyle: { color: "transparent" },
        data: upper,
        z: -1,
      } as any);
      series.push({
        name: `_band_lower_${yKey}`,
        type: "line",
        showSymbol: false,
        silent: true,
        yAxisIndex: yAxisIdx,
        lineStyle: { width: 1, color: "#94a3b8" },
        itemStyle: { color: "transparent" },
        data: lower,
        z: -1,
      } as any);
    }
  }

  // Guardrail bound: one horizontal line at the level the metric must stay
  // above (gte) or below (lte). The level itself rides in the comparison rows
  // — every bucket of a `level` allocation carries the same number — so the
  // first finite value IS the bound; no separate field to drift from it.
  // Whichever slot the guardrail arrived in: its own (`target_rows`, beside a
  // shifted leg) or the shared one (`comparison_rows`, as the only leg).
  const guardrailRows = hasOwnTargetSlot ? targetSeriesRows : comparisonSeriesRows;
  if (isGuardrail && guardrailRows.length && yKeys.length) {
    const boundKey = yKeys[0];
    let bound: number | null = null;
    for (const r of guardrailRows) {
      const v = r[boundKey];
      if (v == null) continue;
      const n = toNum(v);
      if (Number.isFinite(n)) { bound = n; break; }
    }
    const anchor = series.find((sr: any) => !String(sr.name ?? "").startsWith("_"));
    if (bound != null && anchor) {
      (anchor as any).markLine = {
        silent: true,
        symbol: "none",
        // Per-ITEM lineStyle, not a container-level one: the incomplete-period
        // line merges into this same `data` array below and must keep its own
        // amber dashes rather than inherit these.
        data: [
          {
            yAxis: bound,
            lineStyle: { color: GUARDRAIL_LINE_COLOR, type: "dashed" as const, width: 1.5 },
            label: {
              formatter: "Guardrail",
              position: "insideEndTop" as const,
              fontSize: 10,
              color: GUARDRAIL_LINE_COLOR,
            },
          },
        ],
      };
    }
  }

  // The "not filled in yet" zone — one vocabulary, two producers:
  //  (1) a time axis whose buckets after the last data point up to `through`
  //      are BEHIND (ADR-0266): the zone spans that run; buckets after
  //      `through` are future and stay bare, only the comparison draws there;
  //  (2) `period_completeness`: the bucket it names is partly inside the
  //      expected days — dashed tail over the last two points and the zone
  //      on that bucket. Skipped when (1) already covers the bucket.
  // The zone hangs on the first non-helper cartesian series of ANY type: on
  // a bar chart `firstLine` used to be undefined and the zone silently drew
  // nothing (review R4-code-quality M1).
  const zoneAnchor = series.find(
    (s: any) => (s.type === "line" || s.type === "bar") && !s.name?.startsWith("_"),
  );
  const attachZone = (fromIndex: number, toIndex: number) => {
    if (!zoneAnchor) return;
    const rowCount = (spec.rows ?? []).length;
    const zoneMarkArea = buildIncompleteZoneMarkArea(rowCount, fromIndex, toIndex);
    if (zoneMarkArea) {
      // Merge with existing markArea if present
      const existing = (zoneAnchor as any).markArea;
      if (existing?.data) {
        existing.data.push(...zoneMarkArea);
      } else {
        (zoneAnchor as any).markArea = { silent: true, data: zoneMarkArea };
      }
    }
    const zoneLine = buildIncompleteZoneMarkLine(rowCount, fromIndex);
    if (zoneLine) {
      // MERGE, never assign. An assignment drops whatever markLine was
      // already attached — today that is the guardrail bound, which would
      // vanish on exactly the charts that show an unfinished period. The
      // markArea block just above has always merged; this is the same rule.
      // Each entry carries its own lineStyle, so the two lines keep their
      // own looks inside one container.
      const existingLine = (zoneAnchor as any).markLine;
      const zoneData = (zoneLine.data as any[]).map((d: any) => ({
        ...d,
        lineStyle: zoneLine.lineStyle,
      }));
      if (existingLine?.data) {
        existingLine.data.push(...zoneData);
      } else {
        (zoneAnchor as any).markLine = zoneLine;
      }
    }
  };

  let behindFrom = -1;
  let behindTo = -1;
  if (axis && (spec.rows ?? []).length >= 2) {
    const rows = spec.rows ?? [];
    let lastWithData = -1;
    let lastExpected = -1;
    rows.forEach((r, idx) => {
      if (yKeys.some((k) => measuredNumber(r[k]) !== null)) lastWithData = idx;
      if (toStr(r[xKey]).slice(0, 10) <= axis.through) lastExpected = idx;
    });
    if (lastWithData + 1 <= lastExpected) {
      behindFrom = lastWithData + 1;
      behindTo = lastExpected;
      attachZone(behindFrom, behindTo);
    }
  }

  // Incomplete period: dashed last segment overlay + zone markArea/markLine
  const pc = spec.period_completeness;
  if (pc && incompleteIndex >= 0 && (spec.rows ?? []).length >= 2) {
    const rows = spec.rows ?? [];

    // Add dashed overlay series for each line series (the two points ending
    // on the incomplete bucket)
    for (let i = 0; i < yKeys.length; i++) {
      const k = yKeys[i];
      const cfg = seriesConfigMap.get(k);
      const sType = cfg?.type ?? defaultSeriesType;
      if (sType !== "line") continue;

      const color = overlayColorOf(k) ?? distributedColorAt(i);
      const dashData = rows.map((r, idx) => {
        if (idx === incompleteIndex - 1 || idx === incompleteIndex) {
          return axis ? measuredNumber(r[k]) : toNum(r[k]);
        }
        return null; // only draw the two points ending on the incomplete bucket
      });

      series.push({
        name: `_incomplete_${k}`,
        type: "line",
        showSymbol: false,
        silent: true,
        lineStyle: { color, width: 1.5, type: "dashed" as const },
        itemStyle: { color: "transparent" },
        areaStyle: undefined,
        data: dashData,
        z: 3,
        connectNulls: false,
      } as any);
    }

    // The zone on the incomplete bucket — unless the behind-run already
    // covers it (a partial month with no data yet is behind AND partial; one
    // zone says it).
    const covered = behindFrom >= 0 && incompleteIndex >= behindFrom && incompleteIndex <= behindTo;
    if (!covered) attachZone(incompleteIndex, incompleteIndex);
  }

  // Get format for secondary axis
  const secondaryFormat = spec.secondary_y_axis?.format;

  // Get format for primary axis from backend, or fall back to auto-detection
  // For grouped-bar charts: use metric_format (the original metric's format before pivot)
  const primaryFormat = spec.primary_y_axis?.format
    ?? (spec as any).metric_format
    ?? (isPercentage && !hasDualAxis ? "fraction" : undefined)
    ?? (isAlreadyPct && !hasDualAxis ? "percentage" : undefined);

  // Determine optimal rotation based on number of x-axis items and label lengths
  const xCount = x.length;
  const avgLabelLength = x.reduce((sum, l) => sum + l.length, 0) / Math.max(x.length, 1);
  // Use rotation for many items or long labels, otherwise try horizontal
  // A step waterfall's categories are its steps, named by the author ("Not
  // in the steps (CM1)" included): horizontal and wrapped they stay legible
  // up to twelve (the McGregor bridge has twelve), where a 45° slant eats
  // the tile's height and leaves the plot a sliver (browser gate A6).
  const useRotation =
    chartType === "metric_waterfall"
      ? xCount > 12 || spec.waterfall_compact === true
      : xCount > 6 || avgLabelLength > 8;
  const xAxisRotate = useRotation ? 45 : 0;

  // Format x-axis labels (truncate/wrap if needed)
  const formattedX = useRotation
    ? x // Keep original for rotated labels (they have more space)
    : x.map(label => formatAxisLabel(label, 14));

  // Determine legend position: explicit override or auto-detect from series count
  // A guardrail draws no overlay SERIES (it is a markLine), so counting one
  // per metric here would push the legend to the right on a chart that has
  // half the entries it thinks it has.
  const legendItemCount = yKeys.length * (drawsComparisonOverlay ? 2 : 1);
  // A step waterfall draws one series whose categories ARE the legend: a
  // legend entry would be a nameless swatch taking the plot's height.
  const legendHidden = spec.legend_position === "none" || chartType === "metric_waterfall";
  // A page legend is ALWAYS under the plot: the right-hand column is a scroll
  // legend's home, and a page neither scrolls nor pages. Forcing it here is
  // what gives the page the bottom geometry everywhere it is derived — manual
  // margins, no 120px legend column, the x-axis name gap.
  const legendAtBottom = page !== null || (spec.legend_position
    ? spec.legend_position === "bottom" || spec.legend_position === "top" || spec.legend_position === "none"
    : legendItemCount <= 5);

  // Calculate dynamic margins
  // When legend at bottom: x-axis labels + legend space
  // When legend on right: minimal margin (containLabel handles the rest)
  // A 45° label's vertical extent is its drawn width × sin 45° plus a line
  // of text and the axis margin. The width is capped by `axisLabel.width`
  // (80px when rotated, truncating beyond it), so a long label needs ~73px;
  // the fixed 50 that stood here let "Organic Search" or "Comparison period"
  // run into a bottom legend. Never less than 50, so a chart with short
  // labels keeps its geometry.
  const ROTATED_LABEL_MAX_PX = 80;
  const longestLabelPx = Math.min(
    ROTATED_LABEL_MAX_PX,
    x.reduce((m, l) => Math.max(m, String(l ?? "").length), 0) * 6.5,
  );
  const xAxisLabelHeight = useRotation
    ? Math.max(50, Math.ceil((longestLabelPx + 12) * Math.SQRT1_2 + 8))
    : 24;
  const bottomMargin = legendAtBottom
    ? xAxisLabelHeight + 26  // labels + legend
    : useRotation ? 8 : 4;   // minimal margin, containLabel does the rest
  const baseLeftMargin = calculateLeftMargin(allYValues, primaryFormat);
  // Add extra space for axis title if present
  const hasLeftAxisTitle = !!(spec.primary_y_axis?.label || spec.y_label);
  const leftMargin = baseLeftMargin + (hasLeftAxisTitle ? 20 : 0);
  // Add extra space for legend on the right only when legend is on the right
  const legendWidth = legendAtBottom ? 0 : 120;
  // Right axis margin: smaller than left since secondary values are often percentages
  const baseRightAxisMargin = hasDualAxis ? Math.min(calculateLeftMargin(allYValues, secondaryFormat), 40) : 16;
  const hasRightAxisTitle = hasDualAxis && !!spec.secondary_y_axis?.label;
  const axisMargin = baseRightAxisMargin + (hasRightAxisTitle ? 12 : 0);
  const rightMargin = axisMargin + legendWidth;

  // Build Y-axis configuration
  const yAxisConfig: any[] = [
    {
      type: "value",
      name: spec.primary_y_axis?.label ?? spec.y_label ?? "",
      nameLocation: "middle",
      nameGap: baseLeftMargin + 12,
      position: "left",
      // Every column of a 100% stack ends at exactly 1: the axis ends there
      // too, instead of ECharts' "nice" 120% (browser gate S8).
      ...(isHundredPercentStack ? { min: 0, max: 1 } : {}),
      axisLine: { show: false },
      axisTick: { show: false },
      splitLine: { lineStyle: { color: "#e0e0e0", type: [6, 4] as any } },
      axisLabel: {
        color: AXIS_LABEL_COLOR,
        formatter: (value: number) => formatYAxisLabel(value, primaryFormat),
      },
    },
  ];

  if (hasDualAxis) {
    yAxisConfig.push({
      type: "value",
      name: spec.secondary_y_axis?.label ?? "",
      nameLocation: "middle",
      nameGap: baseRightAxisMargin + 8,
      position: "right",
      axisLine: { show: false },
      axisTick: { show: false },
      splitLine: { show: false },
      min: spec.secondary_y_axis?.min,
      max: spec.secondary_y_axis?.max,
      axisLabel: {
        color: AXIS_LABEL_COLOR,
        formatter: (value: number) => formatYAxisLabel(value, secondaryFormat),
      },
    });
  }

  // For horizontal bar charts, swap axes
  if (isHorizontal) {
    // Calculate left margin based on category label lengths
    const maxLabelLen = formattedX.reduce((max, l) => Math.max(max, l.length), 0);
    const horizontalLeftMargin = Math.max(50, Math.min(maxLabelLen * 7 + 16, 150));

    // Period comparison: horizontal-grouped-bar with 2+ series
    const isHorizontalGrouped = chartType === "horizontal-grouped-bar";
    const deltaBase = spec.delta_base_index;
    if (isHorizontalGrouped && series.length >= 2 && deltaBase != null && yKeys.length >= 2) {
      const formatBarValue = (value: number) => formatYAxisLabel(value, primaryFormat);

      // Per-row delta: use pre-computed _abs_change/_pct_change if available,
      // otherwise compute from the two series values
      const rows = spec.rows ?? (spec as any).data ?? [];
      const deltas = rows.map((row: any) => {
        const hasPrecomputed = row._abs_change != null || row._pct_change != null;
        const prev = toNum(row[yKeys[deltaBase]]);
        const cur = toNum(row[yKeys[1 - deltaBase]]);
        const absVal = hasPrecomputed ? toNum(row._abs_change) : cur - prev;
        if (prev === 0 && cur === 0) {
          return { text: "0", color: "#666" };
        }
        if (prev === 0 && !hasPrecomputed) {
          return { text: cur > 0 ? "+∞" : "-∞", color: cur > 0 ? "#2e7d32" : "#c62828" };
        }
        const pct = hasPrecomputed && row._pct_change != null
          ? toNum(row._pct_change)
          : prev !== 0 ? (absVal / prev) * 100 : null;
        const sign = absVal >= 0 ? "+" : "";
        const absPart = `${sign}${smartNumber(absVal, 1)}`;
        const pctPart = pct != null ? `(${pct >= 0 ? "+" : ""}${smartNumber(pct, 1)}%)` : "";
        return {
          text: pctPart ? `${absPart} ${pctPart}` : absPart,
          color: absVal >= 0 ? "#2e7d32" : "#c62828",
        };
      });

      const comparisonZoom = horizontalBarDataZoom(formattedX.length, [0, 1]);
      // This branch's legend is already plain, horizontal and at the bottom,
      // so a page only has to say how much room its rows need. The names are
      // the two metric series: the delta overlay is `__delta__`, a helper, and
      // naming the data here keeps it out of the page's legend.
      const hzCompNames = series
        .map((s: any) => s.name)
        .filter((n: any) => !isHelperSeriesName(n));
      const hzCompPage = page ? pageLegendGeometry(hzCompNames, page.widthPx) : null;
      // Series order below: [first, delta overlay, second, ...rest].
      const hzGroupedKeyOf = (i: number) =>
        preparedSeriesBuilder ? undefined : i === 0 ? yKeys[0] : i === 2 ? yKeys[1] : undefined;
      return withDatums({
        ...chartAnimation("horizontal-bar", motion),
        grid: {
          left: horizontalLeftMargin,
          right: comparisonZoom.length > 0 ? 120 : 105, // extra space for scrollbar + dual delta labels
          top: 8,
          bottom: hzCompPage ? Math.max(36, pageLegendReservePx(hzCompPage, page!.heightPx)) : 36, // space for legend
          containLabel: false,
        },
        ...(comparisonZoom.length > 0 ? { dataZoom: comparisonZoom } : {}),
        tooltip: {
          trigger: "axis",
          axisPointer: { type: "shadow" },
          // Product-image tooltips are taller than the chart's padding —
          // render on document.body so they overlap neighbouring dashboard
          // cards instead of clipping at the card boundary.
          appendTo: "body",
          textStyle: { fontSize: TOOLTIP_FONT_SIZE },
          formatter: buildBarTooltipFormatter(spec.rows ?? [], {
            formatValue: (v) => formatTooltipValue(v, primaryFormat),
            rowPreviews,
          }),
        },
        legend: hzCompPage
          ? { ...hzCompPage.legend, data: hzCompNames }
          : {
              type: "plain",
              orient: "horizontal" as const,
              bottom: 4,
              left: "center",
              textStyle: { fontSize: 11 },
            },
        yAxis: [
          {
            type: "category",
            data: formattedX,
            inverse: true,
            axisLabel: {
              overflow: "truncate",
              width: horizontalLeftMargin - 16,
              formatter: (value: string) => formatAxisLabel(value, 20),
            },
          },
          {
            // Hidden duplicate axis for delta overlay
            type: "category",
            data: formattedX,
            inverse: true,
            show: false,
          },
        ],
        xAxis: [
          {
            type: "value",
            show: false, // values shown as bar labels
          },
          {
            // Hidden duplicate axis for delta overlay
            type: "value",
            show: false,
          },
        ],
        series: [
          // First series (top bar): value inside bar (white), delta to the right
          {
            ...series[0],
            type: "bar" as const,
            barMaxWidth: 40,
            label: { show: false }, // per-item labels below
            data: (() => {
              const allVals: number[] = (series[0] as any).data ?? [];
              const maxVal = Math.max(...allVals.map(Math.abs), 1);
              return allVals.map((val: number, idx: number) => {
                const d = deltas[idx];
                const isShort = Math.abs(val) / maxVal < 0.15;
                if (isShort) {
                  return {
                    value: val,
                    label: {
                      show: true,
                      position: "right" as const,
                      distance: 4,
                      formatter: () => `{valDark|${formatBarValue(val)}}  {delta|${d?.text ?? ""}}`,
                      rich: {
                        valDark: { fontSize: 11, color: "#333", fontWeight: "bold" as const },
                        delta: { fontSize: 10, color: d?.color ?? "#666", fontWeight: "bold" as const },
                      },
                    },
                  };
                }
                return {
                  value: val,
                  label: {
                    show: true,
                    position: "insideRight" as const,
                    distance: 8,
                    formatter: () => formatBarValue(val),
                    fontSize: 11,
                    color: "#fff",
                    fontWeight: "bold" as const,
                  },
                };
              });
            })(),
          },
          // Delta overlay on a hidden second xAxis: all bars extend to per-row max
          // so delta labels align vertically on the right edge
          {
            name: "__delta__",
            type: "bar" as const,
            xAxisIndex: 1,
            yAxisIndex: 1,
            barMaxWidth: 40,
            itemStyle: { color: "transparent" },
            tooltip: { show: false },
            legendHoverLink: false,
            data: (() => {
              const vals0: number[] = (series[0] as any).data ?? [];
              const vals1: number[] = (series[1] as any).data ?? [];
              const s0Max = Math.max(...vals0.map(Math.abs), 1);
              // Global max across both series — all overlay bars extend to this
              const globalMax = Math.max(...vals0.map(Math.abs), ...vals1.map(Math.abs), 1);
              return vals0.map((_val: number, idx: number) => {
                const d = deltas[idx];
                const isShort = Math.abs(vals0[idx]) / s0Max < 0.15;
                return {
                  value: globalMax,
                  label: {
                    show: !isShort && !!d?.text,
                    position: "right" as const,
                    distance: 4,
                    formatter: () => d?.text ?? "",
                    fontSize: 10,
                    color: d?.color ?? "#666",
                    fontWeight: "bold" as const,
                  },
                };
              });
            })(),
          },
          // Second series (bottom bar): value inside or outside based on bar width
          {
            ...series[1],
            type: "bar" as const,
            barMaxWidth: 40,
            label: { show: false }, // per-item labels below
            data: (series[1] as any).data?.map((val: number) => {
              // If bar is short (<15% of max), label won't fit inside
              const maxVal = Math.max(...((series[1] as any).data ?? []).map(Math.abs), 1);
              const isShort = Math.abs(val) / maxVal < 0.15;
              return {
                value: val,
                label: {
                  show: true,
                  position: isShort ? ("right" as const) : ("insideRight" as const),
                  formatter: () => formatBarValue(val),
                  fontSize: 11,
                  color: isShort ? "#333" : "#fff",
                  fontWeight: "bold" as const,
                },
              };
            }),
          },
          // Any additional series
          ...series.slice(2).map((s: any) => ({
            ...s,
            type: "bar" as const,
            barMaxWidth: 40,
          })),
        ],
      }, hzGroupedKeyOf);
    }

    // Comparison support for horizontal bars.
    //
    // ONE leg only, on purpose: this path renders the comparison as a per-bar
    // delta LABEL ("€12.3K  +12.2%"), and a label measured against two
    // baselines at once is not a defined picture. `target_rows` therefore
    // draws nothing here — the plan line is built (and skipped) further up,
    // where `drawsTargetOverlay` excludes `isHorizontal` for this reason.
    const hzCompRows = Array.isArray(spec.comparison_rows) ? spec.comparison_rows : [];
    const hzHasComparison = hzCompRows.length > 0;
    const hzCompLabel = spec.comparison_label ?? "prev. period";

    // Compute per-row deltas when comparison is active.
    //
    // `hzCompRows` is index-aligned to `spec.rows` by
    // `utils/comparisonAlignment.ts` — same length, same order — so reading
    // it by position here is correct BECAUSE the alignment already happened,
    // not by assuming the two periods rank their members the same way.
    const hzDeltas: Array<{ text: string; color: string } | null> = hzHasComparison
      ? (spec.rows ?? []).map((row, idx) => {
          const k = yKeys[0];
          const compRow = hzCompRows[idx];
          if (!compRow) return null;
          // Pass the RAW values: "absent" (member not in the comparison
          // period) must stay distinguishable from a real zero downstream.
          return computeComparisonDelta(row[k], (compRow as any)[k], (pct) => smartNumber(pct, 1));
        })
      : [];

    // Shared by both series: the label is rendered by whichever bar is longer
    // in a given row, so its styling cannot live on one of them.
    const hzLabelRich = {
      val: { fontSize: 10, color: "#666" },
      deltaUp: { fontSize: 10, color: DELTA_POSITIVE_COLOR, fontWeight: "bold" as const },
      deltaDown: { fontSize: 10, color: DELTA_NEGATIVE_COLOR, fontWeight: "bold" as const },
      // "Nothing to compare against" is not a rise or a fall, so it stays
      // neutral even now that real changes are coloured.
      deltaFlat: { fontSize: 10, color: DELTA_NEUTRAL_COLOR },
    };

    /** The row's label: always the ACTUAL value + its change, wherever it hangs. */
    const hzRowLabel = (rowIndex: number): string => {
      const primaryKey = yKeys[0];
      const val = toNum((spec.rows ?? [])[rowIndex]?.[primaryKey]);
      if (val === 0 || isNaN(val)) return "";
      const formatted = formatYAxisLabel(val, primaryFormat);
      const delta = hzDeltas[rowIndex];
      if (!delta) return `{val|${formatted}}`;
      return `{val|${formatted}}  {${deltaRichStyle(delta.color)}|${delta.text}}`;
    };

    /** True where the comparison bar is the longer one, so it carries the label. */
    const hzLabelOnComparison: boolean[] = (spec.rows ?? []).map((row, i) => {
      if (!hzHasComparison) return false;
      const primaryKey = yKeys[0];
      const compRaw = (hzCompRows[i] as any)?.[primaryKey];
      if (compRaw == null) return false;
      return Math.abs(toNum(compRaw)) > Math.abs(toNum(row[primaryKey]));
    });

    // Build final series array for horizontal bars
    const hzFinalSeries: any[] = [];
    /** hzFinalSeries index -> the y_key it draws (actual and comparison bar). */
    const hzKeyByIndex = new Map<number, string>();
    for (let i = 0; i < yKeys.length; i++) {
      const k = yKeys[i];
      const color = asDrawnColor(overlayColorOf(k) ?? positionalColor(chartColors, i));
      const rows = spec.rows ?? [];
      const seriesName = spec.labels?.[k] ?? k;

      // Empty column between this metric's pair and the previous one
      if (i > 0 && hzHasComparison) {
        hzFinalSeries.push(comparisonGapSeries(i, rows.length, 50));
      }

      // Actual bar
      hzKeyByIndex.set(hzFinalSeries.length, k);
      hzFinalSeries.push({
        name: seriesName,
        type: "bar",
        barMaxWidth: 50,
        z: hzHasComparison ? 2 : undefined,
        itemStyle: { color: barGradient(color, true), borderRadius: [3, 3, 3, 3] },
        emphasis: { focus: "series" as const },
        label: {
          show: true,
          position: "right" as const,
          fontSize: 10,
          color: "#666",
          // Rich text so the change can carry its own colour while the value
          // stays neutral. One flat string would force both into `label.color`
          // — which is why these deltas used to be grey while the very same
          // percentages in the tables below were green/red (decision Arjen,
          // 2026-08-26). Same mechanism the horizontal-grouped-bar delta
          // labels already use further up.
          formatter: (params: any) => hzRowLabel(params.dataIndex),
          rich: {
            val: { fontSize: 10, color: "#666" },
            deltaUp: { fontSize: 10, color: DELTA_POSITIVE_COLOR, fontWeight: "bold" as const },
            deltaDown: { fontSize: 10, color: DELTA_NEGATIVE_COLOR, fontWeight: "bold" as const },
            // "Nothing to compare against" is not a rise or a fall, so it
            // stays neutral even now that real changes are coloured.
            deltaFlat: { fontSize: 10, color: DELTA_NEUTRAL_COLOR },
          },
        },
        // Per-item: the label rides on whichever of the two bars is LONGER in
        // that row, so it always sits to the RIGHT of both instead of landing
        // on top of the comparison bar (which is what it did when the
        // comparison was the longer one). ECharts positions a label relative
        // to its own bar, so choosing the carrier is the whole fix — no pixel
        // arithmetic, and it stays correct at any width.
        data: rows.map((r: any, i: number) => ({
          value: toNum(r[k]),
          label: { show: !hzLabelOnComparison[i] },
        })),
      });

      // Comparison bar (if active)
      if (hzHasComparison) {
        const lightColor = lightenHex(color, 0.45);
        hzKeyByIndex.set(hzFinalSeries.length, k);
        hzFinalSeries.push({
          name: `${seriesName} (${hzCompLabel})`,
          type: "bar",
          barMaxWidth: 50,
          barGap: COMPARISON_BAR_GAP,
          z: 1,
          itemStyle: { color: lightColor, opacity: 0.5 },
          emphasis: { focus: "series" as const },
          label: {
            show: false,  // per-item below; only the longer bar carries it
            position: "right" as const,
            fontSize: 10,
            color: "#666",
            formatter: (params: any) => hzRowLabel(params.dataIndex),
            rich: hzLabelRich,
          },
          data: hzCompRows.map((r: any, i: number) => {
            const v = r[k];
            if (v == null) return null;
            return {
              value: toNum(v),
              label: { show: hzLabelOnComparison[i] },
            };
          }),
        });
      }
    }

    // Legend names, minus the layout-only gap columns (which have no data and no color)
    const hzLegendData = (hzHasComparison ? hzFinalSeries : series)
      .map((s: any) => s.name)
      .filter((n: any) => !isHelperSeriesName(n));

    // Same number of categories with or without a comparison (decision Arjen,
    // 2026-08-26). This used to drop to 4 so the doubled bar count kept its
    // thickness — but comparison is ON by default, so that silently shrank
    // every top-N tile from 6 rows to 4 the moment the feature started
    // working. A tile showing fewer products because a comparison exists is
    // a worse trade than slightly thinner bars.
    //
    // NOT DONE HERE, deliberately: growing the tile so the doubled series
    // keeps its old thickness. The grid persists rendered heights back to the
    // item (`DashboardGrid.tsx` — onLayoutChange diffs `l.h` against
    // `layout_h`), so a height that follows the comparison toggle would grow
    // the tile permanently, including after the comparison is switched off.
    // Open with Arjen; see the plan's step 8.
    const hzVisibleCats = HORIZONTAL_BAR_VISIBLE_CATEGORIES;
    const normalZoom = horizontalBarDataZoom(formattedX.length, [0], hzVisibleCats);
    const hzRightMargin = hzHasComparison
      ? 130
      : (legendAtBottom ? 16 : legendWidth + 16) + (spec.bar_end_labels ? (activeEndLabel && normalizeBarEndLabels(spec.bar_end_labels).columns.find(c => c.column === activeEndLabel)?.format !== "percentage" ? 70 : 50) : 0) + (normalZoom.length > 0 ? 16 : 0);

    // On a page the legend moves from above the bars to under them: "under the
    // plot" is the page rule, and above the plot a wrapped legend would push
    // the bars down out of a cell that clips.
    const hzPage = page && !legendHidden ? pageLegendGeometry(hzLegendData, page.widthPx) : null;

    const hzKeyOf = (i: number): string | undefined =>
      preparedSeriesBuilder ? undefined : hzHasComparison ? hzKeyByIndex.get(i) : i < yKeys.length ? yKeys[i] : undefined;
    return withDatums({
      ...chartAnimation("horizontal-bar", motion),
      grid: {
        left: horizontalLeftMargin,
        right: hzRightMargin,
        top: page ? 8 : legendAtBottom ? 28 : 8,
        bottom: hzPage ? pageLegendReservePx(hzPage, page!.heightPx) : 8,
        containLabel: false,
      },
      ...(normalZoom.length > 0 ? { dataZoom: normalZoom } : {}),
      tooltip: {
        trigger: "axis",
        axisPointer: { type: "shadow" },
        // Product-image tooltips are taller than the chart's padding — render
        // on document.body so they overlap neighbouring dashboard cards
        // instead of clipping at the card boundary.
        appendTo: "body",
        textStyle: { fontSize: TOOLTIP_FONT_SIZE },
        // Resolve the hovered row by dataIndex into spec.rows — the SAME array
        // that built formattedX (yAxis.data) and the series data — so duplicate
        // product titles map to the correct row/image. Escapes all interpolated
        // values (ECharts renders the string via innerHTML) and appends the
        // product image when the row maps to one.
        formatter: buildBarTooltipFormatter(spec.rows ?? [], {
          formatValue: (v) => formatTooltipValue(v, primaryFormat),
          rowPreviews,
          comparisonLabel: hzCompLabel,
          periodLabel: (spec as any).period_label,
        }),
      },
      legend: page
        ? hzPage
          ? { ...hzPage.legend, data: hzLegendData }
          : { type: "plain", show: false, data: hzLegendData }
        : legendHidden
        ? { show: false }
        : legendAtBottom
          ? {
              type: "plain",
              orient: "horizontal",
              top: 0,
              left: "center",
              textStyle: { fontSize: 11 },
              data: hzLegendData,
            }
          : {
              type: "scroll",
              orient: "vertical",
              right: 0,
              top: "middle",
              textStyle: { width: 100, overflow: "break", fontSize: 11 },
              formatter: formatLegendName,
              data: hzLegendData,
            },
      yAxis: {
        type: "category",
        data: formattedX,
        inverse: true,
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: {
          overflow: "truncate",
          width: horizontalLeftMargin - 16,
          formatter: (value: string) => formatAxisLabel(value, 20),
        },
      },
      xAxis: {
        type: "value",
        show: false,
      },
      series: hzHasComparison ? hzFinalSeries : series.map((s, idx) => {
        const isLast = idx === series.length - 1;
        const rawEndLabels = spec.bar_end_labels;
        const hasEndLabels = isLast && rawEndLabels;
        if (!hasEndLabels) {
          return {
            ...s,
            type: "bar" as const,
            barMaxWidth: 50,
            label: {
              show: true,
              position: "right" as const,
              fontSize: 10,
              color: "#666",
              formatter: (params: any) => {
                const val = Number(params.value);
                if (val === 0 || isNaN(val)) return "";
                return formatYAxisLabel(val, primaryFormat);
              },
            },
          };
        }
        const normalized = normalizeBarEndLabels(rawEndLabels);
        const activeCol = normalized.columns.find(
          (c) => c.column === (activeEndLabel ?? normalized.default)
        ) ?? normalized.columns[0];
        return {
          ...s,
          type: "bar" as const,
          barMaxWidth: 50,
          label: {
            show: true,
            position: "right",
            formatter: (params: any) => {
              const row = (spec.rows ?? spec.data ?? [])[params.dataIndex];
              if (!activeCol) return "";
              const val = row?.[activeCol.column];
              if (val == null) return "";
              if (activeCol.format === "percentage")
                return `${smartNumber(Number(val), 1)}%`;
              if (activeCol.format === "currency")
                return formatYAxisLabel(Number(val), "currency");
              return String(smartNumber(Number(val), 1));
            },
            fontSize: 11,
            color: "#666",
          },
        };
      }),
    }, hzKeyOf);
  }

  // ---- Pair every comparison bar with its own actual -------------------------
  // Built order is [A, B, A', B']; rendered order becomes [A, A', gap, B, B'] so
  // a metric and its previous-period bar read as one block. The gap is an empty
  // bar column (see COMPARISON_BAR_GAP) — ECharts has only one barGap per axis.
  //
  // Comparison series that start out hidden (many metrics, see legend.selected
  // below) would leave the gaps standing without a bar next to them, so pairs are
  // only spaced apart when the comparisons are actually shown. Stacked bars share
  // a single column, so there is nothing to pair there either.
  const comparisonHiddenByDefault = hasComparison && yKeys.length > 3;
  const spaceOutPairs = drawsComparisonOverlay && !comparisonHiddenByDefault && !isStacked;
  const pairedSeries: any[] = [];
  /** series index -> which y_key it draws, and whether it is the comparison of it. */
  const seriesMetaByIndex = new Map<number, { yKey: string; isComparison: boolean }>();
  const pairedIndices = new Set<number>();
  if (preparedSeriesBuilder) {
    /**
     * A prepared builder decides its own series list, in its own order, and
     * has no comparison overlay to pair (`drawsComparisonOverlay`). The one
     * thing still owed is the meta map: the tooltip reads it by series INDEX
     * and skips any index it has no entry for (`if (!meta) continue`), so the
     * `y_keys[i] -> series[i]` mapping the loop below does would name a
     * waterfall's invisible `_waterfall_base` and silently drop the bar the
     * reader is pointing at. Helper series (`_`-prefixed) get no entry here
     * for exactly that reason; the rest take the y_keys in order.
     */
    let metricIdx = 0;
    for (let i = 0; i < series.length; i++) {
      pairedSeries.push(series[i]);
      if (isHelperSeriesName(series[i]?.name)) continue;
      const k = yKeys[metricIdx++];
      if (k != null) seriesMetaByIndex.set(i, { yKey: k, isComparison: false });
    }
  } else {
    for (let i = 0; i < yKeys.length; i++) {
      const k = yKeys[i];
      const compIndex = comparisonSeriesIndexByKey.get(k);
      const isBarPair = seriesTypeByKey.get(k) === "bar";
      const prevWasBarPair = i > 0 && seriesTypeByKey.get(yKeys[i - 1]) === "bar";
      if (spaceOutPairs && isBarPair && prevWasBarPair) {
        pairedSeries.push(comparisonGapSeries(i, (spec.rows ?? []).length, 50));
      }
      if (series[i]) {
        pairedIndices.add(i);
        seriesMetaByIndex.set(pairedSeries.length, { yKey: k, isComparison: false });
        pairedSeries.push(series[i]);
      }
      if (compIndex != null && series[compIndex]) {
        pairedIndices.add(compIndex);
        seriesMetaByIndex.set(pairedSeries.length, { yKey: k, isComparison: true });
        pairedSeries.push(series[compIndex]);
      }
      // The plan line sits with its own metric, after the shifted overlay. No
      // gap column is inserted for it (see `seriesTypeByKey`): it is a line.
      // `isComparison: true` puts it under the tooltip's divider, with the
      // other leg — both answer "against what", the actual answers "what".
      const targetIndex = targetSeriesIndexByKey.get(k);
      if (targetIndex != null && series[targetIndex]) {
        pairedIndices.add(targetIndex);
        seriesMetaByIndex.set(pairedSeries.length, { yKey: k, isComparison: true });
        pairedSeries.push(series[targetIndex]);
      }
    }
    // Everything the two loops above did not claim — std-dev bands, incomplete-period
    // overlays — keeps its original relative order at the end.
    for (let i = 0; i < series.length; i++) {
      if (!pairedIndices.has(i)) pairedSeries.push(series[i]);
    }
  }

  // The legend's own names decide how many rows it wraps onto, and the names
  // are only settled once the comparison legs are paired — so the page
  // geometry is computed here rather than beside `bottomMargin`. A hidden
  // legend keeps the margin it has always had: `legend_position: "none"` is
  // about what is drawn, and a page must not quietly grow the plot into space
  // the stored block was sized without.
  // ---- The total of a stacked metrics chart (plan 2026-10-17, D5(a)) --------
  // The column IS the total, so the total is printed, not drawn: a label above
  // each column (absolute variant only — every `_100` column is 100%) and a
  // "Total" line in the tooltip (both variants). The label rides its own
  // invisible helper series rather than the last part: with same-sign
  // stacking a negative part hangs below zero, and a label on its segment
  // would land under the axis inside the first part; a part hidden through
  // the legend would take a label anchored to it away. The helper is
  // `_`-prefixed, so it gets no legend entry and no `seriesMetaByIndex` entry
  // (the tooltip and the click datums skip it).
  const partsFormat = isPartsStack ? partsTotalFormat(spec, yKeys, isHundredPercentStack) : undefined;
  // A part the reader hid through the legend takes the labels away (Arjen,
  // gate S8): they are the total of every part, the column is not any more.
  const aPartIsHidden =
    legendSelected != null &&
    pairedSeries.some((s: any) => !isHelperSeriesName(s.name) && legendSelected[s.name] === false);
  if (
    isPartsStack &&
    !isHundredPercentStack &&
    !aPartIsHidden &&
    (spec.rows ?? []).length <= MAX_TOTAL_LABEL_PERIODS
  ) {
    pairedSeries.push(partsTotalLabelSeries(spec.rows ?? [], yKeys, partsFormat));
  }

  const legendNames = pairedSeries.filter((s: any) => !isHelperSeriesName(s.name)).map((s: any) => s.name);
  // Hide comparison series by default when there are many main series; the
  // reader's own legend clicks (`legendSelected`) win over that default.
  const legendSelection =
    comparisonHiddenByDefault || legendSelected
      ? {
          selected: {
            ...(comparisonHiddenByDefault
              ? Object.fromEntries(
                  pairedSeries.map((s: any) => [s.name, !s.name.includes(`(${comparisonLabel})`)]),
                )
              : {}),
            ...(legendSelected ?? {}),
          },
        }
      : {};
  const pageGeometry = page && !legendHidden ? pageLegendGeometry(legendNames, page.widthPx) : null;
  const gridBottom = pageGeometry ? xAxisLabelHeight + pageLegendReservePx(pageGeometry, page!.heightPx) : bottomMargin;

  return withDatums({
    ...chartAnimation(isCombo ? "combo" : isLine ? "line" : "bar", motion),
    grid: {
      left: leftMargin,
      right: rightMargin,
      top: 8,
      bottom: gridBottom,
      containLabel: !legendAtBottom,  // Manual margins when legend at bottom
    },
    tooltip: {
      trigger: "axis",
      confine: true,
      textStyle: { fontSize: TOOLTIP_FONT_SIZE },
      axisPointer: {
        type: isCombo || hasDualAxis ? "cross" : "shadow",
        crossStyle: { color: "#ccc" },
      },
      formatter: (params: any) => {
        if (!Array.isArray(params)) return "";
        const mainLines: string[] = [];
        const compLines: string[] = [];
        let partsTotal: number | null = null;
        for (const p of params) {
          // Which y_key a hovered series belongs to is looked up, not computed from
          // its position: with comparison bars interleaved between their actuals
          // (and gap columns in between) index arithmetic is silently wrong.
          const meta = seriesMetaByIndex.get(p.seriesIndex);
          if (!meta) continue; // helper series (gap column, band, incomplete overlay)
          const isCompSeries = meta.isComparison;
          const seriesKey = meta.yKey;
          const config = seriesConfigMap.get(seriesKey);
          // Same rule as the series stamping — a key that never got axis 1 must
          // not be formatted as if it had.
          const isSecondary = resolveYAxisIndex(config, seriesKey) === 1;
          const axisFmt = isSecondary ? secondaryFormat : (primaryFormat || (isPercentage ? "fraction" : undefined));
          // A stacked metrics chart formats every part like its total: the
          // parts' own catalog format (absolute), or the share (`_100`).
          const fmt = isPartsStack ? (partsFormat ?? axisFmt) : axisFmt;
          // A waterfall's visible bar carries |v| — the stack needs a positive
          // magnitude — so `p.value` is the height drawn, not the contribution
          // the reader asked about. A -150 step would read as "150". The
          // signed number is the row's, so the row is where it comes from.
          // A hole is a hole in the tooltip too: `Number(null)` is 0, and a
          // bucket with no data would read as "0" (review R4-code-quality M2).
          const hoveredValue = isWaterfall
            ? chartType === "metric_waterfall"
              ? measuredNumber((spec.rows ?? [])[p.dataIndex]?.[seriesKey])
              : toNum((spec.rows ?? [])[p.dataIndex]?.[seriesKey])
            : p.value == null ? null : Number(p.value);
          const value =
            hoveredValue == null || Number.isNaN(hoveredValue)
              ? "–"
              : formatTooltipValue(hoveredValue, fmt);
          // ECharts assigns a formatter's return to `el.innerHTML`
          // (TooltipHTMLContent.js), so every value interpolated here is an
          // injection surface — `seriesName` is `spec.labels?.[k]`, which a
          // member authors and the write path does not constrain. Same rule
          // and same helper as the bar formatter in chartTooltip.ts.
          // `p.marker` stays raw: ECharts-generated HTML (a coloured dot),
          // exactly as chartTooltip.ts:235 documents.
          const line = `${p.marker} ${escapeHtml(String(p.seriesName ?? ""))}: ${escapeHtml(value)}`;
          if (isCompSeries) compLines.push(line);
          else {
            mainLines.push(line);
            if (hoveredValue != null && !Number.isNaN(hoveredValue)) {
              partsTotal = (partsTotal ?? 0) + hoveredValue;
            }
          }
        }
        // The net sum of the parts the reader sees (a part hidden through the
        // legend is not in `params`), formatted like the parts. The label
        // above the column stops at `MAX_TOTAL_LABEL_PERIODS`; this line
        // carries the total at every size.
        if (isPartsStack && partsTotal != null) {
          mainLines.push(`<strong>Total</strong>: ${escapeHtml(formatTooltipValue(partsTotal, partsFormat))}`);
        }
        const header = params[0]?.axisValueLabel ?? params[0]?.name ?? "";
        const parts = [`<strong>${escapeHtml(String(header))}</strong>`, ...mainLines];
        if (compLines.length > 0) {
          parts.push('<hr style="margin:4px 0;border:none;border-top:1px solid #e0e0e0"/>');
          parts.push(...compLines);
        }
        // Show highlight reason if available
        if (activeHighlights.length && params[0]?.dataIndex != null) {
          const rawIdx = toStr((spec.rows ?? [])[params[0].dataIndex]?.[spec.chart_index_by]);
          const hl = activeHighlights.find((h) => h.type === "point" && h.index_value === rawIdx);
          if (hl) {
            const color = sentimentColor(hl.sentiment);
            // `color` needs no escaping: sentimentColor falls back to a
            // constant for any unknown sentiment (highlightUtils.ts), so it
            // cannot break out of the style attribute. `reason` is free text
            // off the stored spec and does.
            parts.push(`<div style="margin-top:4px;padding:3px 6px;border-radius:3px;background:${color}22;color:${color};font-size:11px">${escapeHtml(String(hl.reason ?? ""))}</div>`);
          }
        }
        // Show incomplete period info on last data point
        if (pc && params[0]?.dataIndex === (spec.rows ?? []).length - 1) {
          parts.push(`<div style="margin-top:4px;padding:3px 6px;border-radius:3px;background:${INCOMPLETE_PERIOD_COLOR}22;color:#B45309;font-size:11px">${escapeHtml(String(pc.label ?? ""))}</div>`);
        }
        return parts.join("<br/>");
      },
    },
    legend: page
      ? {
          ...(pageGeometry ? pageGeometry.legend : { type: "plain" as const, show: false }),
          data: legendNames,
          ...legendSelection,
        }
      : {
          type: "scroll",
          data: legendNames,
          ...(legendHidden
            ? { show: false }
            : legendAtBottom
              ? { orient: "horizontal" as const, bottom: 4, left: "center", textStyle: { fontSize: 11 } }
              : { orient: "vertical" as const, right: 0, top: "middle", textStyle: { width: 100, overflow: "break", fontSize: 11 }, formatter: formatLegendName }
          ),
          ...legendSelection,
        },
    xAxis: {
      type: "category",
      data: formattedX,
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: {
        color: "#999",
        rotate: xAxisRotate,
        interval: calculateXAxisInterval(x.length),
        overflow: "truncate",
        width: useRotation ? ROTATED_LABEL_MAX_PX : 100,
        formatter: pc
          ? (value: string, index: number) => {
              // Amber + bold for the incomplete period's label
              if (index === incompleteIndex) {
                return `{incomplete|${useRotation ? value : formatAxisLabel(value, 14)}}`;
              }
              return useRotation ? value : formatAxisLabel(value, 14);
            }
          : useRotation ? undefined : (value: string) => formatAxisLabel(value, 14),
        ...(pc ? {
          rich: {
            incomplete: {
              color: INCOMPLETE_PERIOD_COLOR,
              fontWeight: 600,
            },
          },
        } : {}),
      },
      name: spec.x_label ?? "",
      nameLocation: "middle",
      nameGap: legendAtBottom ? gridBottom - 8 : 24,
    },
    yAxis: hasDualAxis ? yAxisConfig : yAxisConfig[0],
    series: pairedSeries,
  }, (i) => (preparedSeriesBuilder ? undefined : seriesMetaByIndex.get(i)?.yKey));
}

